if (!customElements.get('custom-cart')) {

  // ===================================================================
  // Delivery-date blackout rules (scoped to this component — NOT global,
  // so these identifiers can never collide with other scripts on the
  // page, and the minifier is free to mangle them).
  // -------------------------------------------------------------------
  // All ranges are INCLUSIVE of both their start and end day and are
  // compared on whole-day, LOCAL-time boundaries. Parsing/normalising to
  // local midnight (not `new Date('YYYY-MM-DD')`, which is UTC) keeps the
  // blocked span identical to the dates listed here — no timezone drift,
  // no off-by-one at the edges. They layer ON TOP of the always-on
  // weekend block and never widen it.
  // ===================================================================

  // Parse a 'YYYY-MM-DD' string into a Date at LOCAL midnight.
  // (const, not `function`, so it stays block-scoped and never leaks to
  // global scope via Annex B function-in-block hoisting.)
  const parseBlackoutDate = (iso) => {
    const [year, month, day] = iso.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // Normalise any Date to LOCAL midnight so comparisons ignore time-of-day.
  const blackoutDayStart = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  };

  // Donor-vendor delivery blackouts (Summer 2026). Keyed by the normalised
  // Shopify product vendor of the donor line(s) in the cart. A cart may
  // hold donors from several vendors; every matching vendor contributes its
  // ranges. Vendors absent from this map have no adjustment this season
  // (e.g. "Maigaard Aarhus").
  const VENDOR_DELIVERY_BLACKOUTS = {
    'ivf-syd':          [['2026-07-13', '2026-08-02']],
    'maigaard odense':  [['2026-07-13', '2026-08-09']],
    'donor network dk': [['2026-07-06', '2026-07-26']],
  };

  // Australia rolling block: when the treatment country is AU, the next 30
  // days are not selectable (earliest selectable day is 31 days out).
  const AU_BLOCKED_DAYS_AHEAD = 30;

  // Vendors that cannot dispatch express shipments. For carts holding a
  // donor from one of these vendors, the express-shipping window (days
  // 3-7) is locked in the datepicker — an express-window date REQUIRES
  // the ExpressShipping product (declining the confirmation clears the
  // date), so the dates must be unselectable, not just the product
  // suppressed. The hint/highlighting are hidden and any ExpressShipping
  // line already in the cart is removed by removeInvalidProducts().
  // Same normalised-vendor keys as VENDOR_DELIVERY_BLACKOUTS.
  const EXPRESS_SHIPPING_BLOCKED_VENDORS = ['juno ivf'];

  // Normalise a vendor string for case/whitespace-insensitive matching.
  const normalizeBlackoutVendor = (vendor) => {
    return (vendor || '').trim().toLowerCase().replace(/\s+/g, ' ');
  };

  // True if this vendor cannot dispatch express shipments.
  const isExpressBlockedVendor = (vendor) => {
    return EXPRESS_SHIPPING_BLOCKED_VENDORS.includes(normalizeBlackoutVendor(vendor));
  };

  // Extra delivery lead time (calendar days) required on top of the
  // standard minimum date when the cart holds a donor from these vendors.
  // Adding whole weeks keeps the earliest date on the same weekday the
  // standard minimum would have landed on. Same normalised keys as
  // VENDOR_DELIVERY_BLACKOUTS.
  const VENDOR_EXTRA_LEAD_TIME_DAYS = {
    'juno ivf': 7,
  };

  // Largest extra lead time required by any donor vendor in the cart
  // (the cart has a single delivery date, so the slowest vendor wins).
  const getVendorExtraLeadTimeDays = (vendors) => {
    return (vendors || []).reduce((max, vendor) => {
      const days = VENDOR_EXTRA_LEAD_TIME_DAYS[normalizeBlackoutVendor(vendor)];
      return days && days > max ? days : max;
    }, 0);
  };

  // Build the inclusive {start, end} Date ranges that apply to the given
  // donor vendors. Unknown vendors contribute nothing.
  const getVendorBlackoutRanges = (vendors) => {
    const ranges = [];
    (vendors || []).forEach(vendor => {
      const rules = VENDOR_DELIVERY_BLACKOUTS[normalizeBlackoutVendor(vendor)];
      if (!rules) {
        return;
      }
      rules.forEach(([start, end]) => {
        ranges.push({ start: parseBlackoutDate(start), end: parseBlackoutDate(end) });
      });
    });
    return ranges;
  };

  // True if `day` falls within any inclusive {start, end} range.
  const isWithinBlackoutRange = (day, ranges) => {
    const current = blackoutDayStart(day);
    return ranges.some(range => current >= range.start && current <= range.end);
  };

  customElements.define("custom-cart", class extends BaseDivElement {
    constructor ()
    {
      super();

      this.loadingDonors = [];
      this.loadingClinics = [];
      this.loadingTreatments = [];
      this.initDivs();
      this.initEvents();
      this.debouncedValidate = debounce(() => this.validate(), 200);
      this.donors = [];
      this.variantCache = new Map(); // Cache for variant lookups
      
      // State tracking for clinic selection
      this.currentClinicId = null;
      this.currentClinicApiData = null;
      this.isChangingClinic = false;
      this.lastClinicChangeTime = 0;
      this.clinicsRequestId = 0;
      this.checkQuotaVariantBusy = false;
      // Single frontend-wide debug switch (?debug / cookie / #debug), core.js.
      this.debugMode = isDebugMode();

      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        window.location.reload()
      });

      subscribe(PUB_SUB_EVENTS.cartLoadingStart, () => {
        this.startLoadingDonor('updateQuantity');
      });

      subscribe(PUB_SUB_EVENTS.cartLoadingEnd, () => {
        this.stopLoadingDonor('updateQuantity');
      });
    }

    debugLog = (...args) => {
      if (this.debugMode) console.log(...args);
    };

    isMot2Variant (title) {
      if (!title) return false;
      return /\bMOT\s+2$/i.test(title.trim());
    }

    // Helper function to extract variant ID from Shopify GraphQL GID
    extractVariantId (gid) {
      if (gid && gid.startsWith('gid://shopify/ProductVariant/')) {
        return gid.split('/').pop();
      }
      return null;
    }

    // Helper method to synchronize clinic state between memory and DOM
    syncClinicState () {
      const currentClinic = this.querySelector('input[name="clinic_selected"]');
      if (currentClinic && currentClinic.value) {
        const clinicValue = currentClinic.value;
        const clinicId = clinicValue.split(':')[0];

        // Only update if there's actually a difference to avoid unnecessary operations
        if (this.currentClinicId !== clinicId) {
          this.debugLog(`[CLINIC-DEBUG] Syncing state: memory ${this.currentClinicId} -> DOM ${clinicId}`);
          this.currentClinicId = clinicId;
        }
      } else if (this.currentClinicId && (!currentClinic || !currentClinic.value)) {
        this.debugLog(`[CLINIC-DEBUG][WARNING] Memory has clinic ${this.currentClinicId} but DOM is empty`);
      }
    }

    // Helper function to get variant by external_id (GraphQL GID) with caching
    async getVariantByExternalId (external_id) {
      // Check cache first
      if (this.variantCache.has(external_id)) {
        console.log('[CACHE] Variant cache hit for', external_id);
        return this.variantCache.get(external_id);
      }

      const variantId = this.extractVariantId(external_id);
      if (variantId) {
        try {
          const variant = await fetch(`/variants/${variantId}.js`)
            .then(response => response.json());
          
          // Cache the result for 5 minutes
          this.variantCache.set(external_id, variant);
          setTimeout(() => this.variantCache.delete(external_id), 5 * 60 * 1000);
          
          return variant;
        } catch (error) {
          console.error('Error fetching variant:', error);
          return null;
        }
      }
      return null;
    }

    initDivs ()
    {
      this.shipmentSettings = this.querySelector(".don-shipment-settings");
      this.storageSettings = this.querySelector(".don-storage-settings");
      this.clinicInput = this.querySelector("#clinic-id");
      this.bankInput = this.querySelector("#bank-id");
      this.cartNextStepButton = this.querySelector(".cart-next-step-button");
      this.cartCheckoutButton = this.querySelector(".cart-checkout-button");
      this.clinicLoaderDiv = this.querySelector(".don-shipment-settings-loader");
      this.clinicErrorDiv = this.querySelector(".don-shipment-settings-error");
      this.bankLoaderDiv = this.querySelector(".don-storage-settings-loader");
      this.bankErrorDiv = this.querySelector(".don-storage-settings-error");
      this.personAddPartner = this.querySelector("#cart-person-add-partner");
      this.addGeneticMatching = this.querySelectorAll('.cart-add-genetic-matching');
      this.removeGeneticMatching = this.querySelectorAll('.cart-remove-genetic-matching');
      this.addPregancyQuota = this.querySelectorAll('.cart-add-quota');
      this.removePregancyQuota = this.querySelectorAll('.cart-remove-quota');
      this.removeTreatment = this.querySelectorAll('.cart-remove-treatment');
      this.donorNameInput = this.querySelector('input[name="donor_name"]');
      this.mot2CompanionRows = this.querySelectorAll('.mot2-companion-section');

      this.mot2CompanionRows.forEach(row => {
        const checkbox = row.querySelector('.mot2-acknowledge-checkbox');
        if (checkbox && !checkbox.classList.contains('js-initialized')) {
          checkbox.classList.add('js-initialized');
          checkbox.addEventListener('change', () => this.debouncedValidate());
        }
      });

      if (this.loadingClinics.length > 0) {
        if (this.querySelector(".don-shipment-settings-loader")) {
          this.querySelector(".don-shipment-settings-loader").classList.remove("hidden");
        }

        if (this.querySelector(".order-summary-loader")) {
          this.querySelector(".order-summary-loader").classList.remove("hidden")
        }
      }

      if (this.loadingDonors.length > 0) {
        if (this.querySelectorAll(".don-table-loader")) {
          this.querySelectorAll(".don-table-loader").forEach(loader => loader.classList.remove("hidden"));
        }

        if (this.querySelector(".order-summary-loader")) {
          this.querySelector(".order-summary-loader").classList.remove("hidden")
        }
      }

      if (this.loadingTreatments.length > 0) {
        this.querySelectorAll('.cart-treatments-loader').forEach(loader => loader.classList.remove("hidden"));

        if (this.querySelector(".order-summary-loader")) {
          this.querySelector(".order-summary-loader").classList.remove("hidden")
        }
      }
    }

    initEvents ()
    {
      this.onBroadcast('startLoadingClinic', () => {
        if (this.querySelector(".order-summary-loader")) {
          this.querySelector(".order-summary-loader").classList.remove("hidden")
        }

        if (this.querySelector(".cart-country-loader")) {
          this.querySelector(".cart-country-loader").classList.remove("hidden")
        }
      });

      this.onBroadcast('startLoadingDonor', () => {
        if (this.querySelector(".order-summary-loader")) {
          this.querySelector(".order-summary-loader").classList.remove("hidden")
        }

        if (this.querySelector(".cart-country-loader")) {
          this.querySelector(".cart-country-loader").classList.remove("hidden")
        }
      });

      this.onBroadcast('startLoadingTreatment', () => {
        if (this.querySelector(".order-summary-loader")) {
          this.querySelector(".order-summary-loader").classList.remove("hidden")
        }

        if (this.querySelector(".cart-country-loader")) {
          this.querySelector(".cart-country-loader").classList.remove("hidden")
        }
      });

      this.onBroadcast('endLoadingClinic', () => {
        if (this.loadingDonors.length === 0 && this.loadingClinics.length === 0 && this.loadingTreatments.length === 0) {
          this.querySelector(".order-summary-loader").classList.add("hidden");
          //this.querySelector(".cart-country-loader").classList.add("hidden");
        }
      });

      this.onBroadcast('endLoadingDonor', () => {
        if (this.loadingDonors.length === 0 && this.loadingClinics.length === 0 && this.loadingTreatments.length === 0) {
          this.querySelector(".order-summary-loader").classList.add("hidden");
          //this.querySelector(".cart-country-loader").classList.add("hidden");
        }
      });

      this.onBroadcast('endLoadingTreatment', () => {
        if (this.loadingDonors.length === 0 && this.loadingClinics.length === 0 && this.loadingTreatments.length === 0) {
          this.querySelector(".order-summary-loader").classList.add("hidden");
          //this.querySelector(".cart-country-loader").classList.add("hidden");
        }
      });

      this.onBroadcast('treatment_country_change', (data) => {
        // The clinic list is country-scoped — rebuild it so the dropdown
        // doesn't keep offering the previous country's clinics.
        if (this.clinicInput) {
          this.startLoadingClinic('countryChange');
          this.loadClinics()
            .catch(error => console.error('[CLINIC-DEBUG][ERROR] Failed to reload clinics:', error))
            .then(() => this.stopLoadingClinic('countryChange'));
        }

        this.updateTreatmentCountry()
          .then(() => {
            this.checkQuotaVariant();
            this.updateReservations();
            this.refreshPregnancyQuotaGate();
            this.refreshStrawSetGate();
          });
      });

      this.onBroadcast('residential_country_change', (data) => {
        this.checkQuotaVariant();
        this.updateReservations();
        this.refreshPregnancyQuotaGate();
      });

      // Safari (most aggressively) restores this page from the back-forward
      // cache on back-navigation — e.g. returning from checkout — with stale
      // cart contents, loaders and in-flight locks frozen mid-state. Reload
      // to rebuild from the real cart.
      window.addEventListener('pageshow', (e) => {
        if (e.persisted) {
          window.location.reload();
        }
      });

      // Express shipping remove button
      document.addEventListener('click', (e) => {
        if (e.target.closest('.don-express-shipping-remove')) {
          e.preventDefault();
          this.removeExpressShipping();
        }
      });

      // Show express shipping banner on load if product is in cart
      this.initExpressShippingBanner();
    }

    startLoadingClinic (name)
    {
      if (this.querySelector('.don-shipment-settings-loader')) {
        this.querySelector('.don-shipment-settings-loader').classList.remove('hidden');
        this.querySelector('.don-shipment-settings').classList.add('is-loading');
      }

      if (this.querySelector('.order-summary-loader')) {
        this.querySelector('.order-summary-loader').classList.remove('hidden')
      }

      this.loadingClinics.push(name);
      broadcast('startLoadingClinic');
      this.debugLog('Start loading clinic', name);
    }

    stopLoadingClinic (name)
    {
      const index = this.loadingClinics.indexOf(name);

      if (index === -1) {
        return;
      }

      this.debugLog('Stop loading clinic', name);
      broadcast('stopLoadingClinic');
      this.loadingClinics.splice(index, 1);

      if (this.loadingClinics.length === 0) {
        if (this.querySelector('.don-shipment-settings-loader')) {
          this.querySelector('.don-shipment-settings-loader').classList.add('hidden');
          this.querySelector('.don-shipment-settings').classList.remove('is-loading');
        }

        broadcast('endLoadingClinic');
        this.debugLog('Loading clinic ended');
      }
    }

    startLoadingDonor (name)
    {
      this.querySelectorAll(".don-table-loader").forEach(loader => loader.classList.remove("hidden"));
      this.loadingDonors.push(name);

      broadcast('startLoadingDonor');
      this.debugLog('Start loading donor', name);
    }

    stopLoadingDonor (name)
    {
      const index = this.loadingDonors.indexOf(name);

      if (index === -1) {
        return;
      }

      this.debugLog('Stop loading donor', name);
      broadcast('stopLoadingDonor');
      this.loadingDonors.splice(index, 1);

      if (this.loadingDonors.length === 0) {
        this.querySelectorAll(".don-table-loader").forEach(loader => loader.classList.add("hidden"));
        broadcast('endLoadingDonor');
        this.debugLog('Loading donor ended');
      }
    }

    startLoadingTreatment (name)
    {
      this.querySelectorAll('.cart-treatments-loader').forEach(loader => loader.classList.remove("hidden"));
      this.loadingTreatments.push(name);

      broadcast('startLoadingTreatment');
    }

    stopLoadingTreatment (name)
    {
      const index = this.loadingTreatments.indexOf(name);

      if (index === -1) {
        return;
      }

      broadcast('stopLoadingTreatment');
      this.loadingTreatments.splice(index, 1);

      if (this.loadingTreatments.length === 0) {
        this.querySelectorAll('.cart-treatments-loader').forEach(loader => loader.classList.add("hidden"));
        broadcast('endLoadingTreatment');
      }
    }

    init ()
    {
      if (this.personAddPartner) {
        this.personAddPartner.addEventListener("change", () => {
          if (this.personAddPartner.checked) {
            this.querySelector(".cart-person-partner-details").classList.remove("hidden");
          } else {
            this.querySelector(".cart-person-partner-details").classList.add("hidden");
          }
        });
      }

      if (this.clinicInput) {
        this.clinicInput.addEventListener("change", e => {
          this.changeClinic();
        });
      }

      this.initLogic();
      this.update();
      this.removeInvalidProducts();
      this.checkCart();
      this.checkReservations();

      // Don't sync clinic state here - let changeClinic() handle it after fetching API data
      // This ensures the early bird discount flag is fetched on first page load
    }

    initLogic ()
    {
      this.debugLog('initLogic started');
      if (this.cartNextStepButton) {
        this.cartNextStepButton.addEventListener("click", async e => {
          e.preventDefault();

          if (this.cartNextStepButton.disabled || this.cartNextStepButton.classList.contains("disabled")) {
            return;
          }

          // Both gates share one /cart?view=custom read (fetchCustomCart
          // dedupes concurrent calls); the PQ gate keeps scroll priority.
          const [quotaMissing, strawSetInvalid] = await Promise.all([
            this.runPregnancyQuotaGate(),
            this.refreshStrawSetGate()
          ]);

          if (quotaMissing) {
            return;
          }

          if (strawSetInvalid) {
            this.scrollToStrawSetError();

            return;
          }

          if (this.clinicInput) {
            this.debugLog(`[CLINIC-DEBUG] Next step clicked - updating clinic properties before navigation`);
            this.debugLog(`[CLINIC-DEBUG] Current clinic input value: ${this.clinicInput.value}`);
            this.debugLog(`[CLINIC-DEBUG] Current clinic hidden value: ${this.querySelector('input[name="clinic_selected"]') ? this.querySelector('input[name="clinic_selected"]').value : 'NOT FOUND'}`);

            // Ensure state is synchronized before updating properties
            this.syncClinicState();

            this.updateCurrentClinicProperties(() => {
              this.updateCurrentBankProperties(() => {
                this.debugLog(`[CLINIC-DEBUG] Navigation to step 2`);
                window.location.href = `${routes.cart_url}?view=step_2`
              });
            });
          } else if (this.querySelector('.cart-person')) {
            this.updatePerson(() => {
              this.checkReservations().then(() => {
                window.location.href = "/checkout";
              });
            });
          }
        });
      }

      if (this.cartCheckoutButton) {
        this.cartCheckoutButton.addEventListener("click", async e => {
          e.preventDefault();

          const [quotaMissing, strawSetInvalid] = await Promise.all([
            this.runPregnancyQuotaGate(),
            this.refreshStrawSetGate()
          ]);

          if (quotaMissing) {
            return;
          }

          if (strawSetInvalid) {
            this.scrollToStrawSetError();

            return;
          }

          window.location.href = "/checkout";
        });
      }

      if (this.clinicInput) {
        const clinicToRemove = this.querySelector('[name="clinic_to_remove"]');

        if (clinicToRemove && clinicToRemove.value) {
          this.removeCurrentShipments(() => {
            this.updateCurrentClinicProperties(() => {
              window.location.reload();
            });
          });
        }
      }

      if (this.bankInput) {
        this.bankInput.addEventListener("change", e => {
          this.changeBank();
        });
        this.querySelectorAll(".bank-storage-time select").forEach(e => e.addEventListener("change", () => this.changeBank()));

        const bankToRemove = this.querySelector('[name="bank_to_remove"]');

        if (bankToRemove && bankToRemove.value) {
          this.removeCurrentBanks(() => {
            this.updateCurrentBankProperties(() => {
              window.location.reload();
            });
          });
        }
      }

      this.querySelectorAll('.cart-remove-donor').forEach(remove => {
        remove.addEventListener('click', e => {
          e.preventDefault();

          this.startLoadingDonor('removeDonor');
          this.removeDonor(remove.dataset.productId, remove.dataset.variantId, remove.dataset.lineItemKey, remove.dataset.itemLocalId, () => {
            window.location.reload();
          });
        });
      });

      this.initClinics();
      this.initBanks();
      this.initGeneticMatching();
      this.initTreatments();
      // Don't init datepicker here - let changeClinic() handle it after fetching API data
      // This ensures early bird discount flag is available when datepicker is created
      this.initTreatmentDate();

      if (this.querySelector(".cart-person")) {
        const form = this.querySelector(".cart-person form");
        [
          form.querySelector('[name="first_name"]'),
          form.querySelector('[name="last_name"]'),
          form.querySelector('[name="add_partner"]'),
          form.querySelector('[name="partner_first_name"]'),
          form.querySelector('[name="partner_last_name"]'),
          form.querySelector('[name="terms"]'),
          form.querySelector('[name="privacy"]'),
          form.querySelector('[name="birthday_day"]'),
          form.querySelector('[name="birthday_month"]'),
          form.querySelector('[name="birthday_year"]'),
          form.querySelector('[name="intended_treatment_date"]'),
          form.querySelector('[name="treatment_date_unknown"]'),
          ...form.querySelectorAll('.cart-person-clinic-terms input')
        ].forEach(e => e && e.addEventListener("change", () => this.update()));
      }
    }

    async initDeliveryDate ()
    {
      if (!this.querySelector('[name="delivery_till"]')) {
        return;
      }

      const delivery = this.querySelector('[name="delivery_till"]');
      let minDate = (new Date()).getTime() + 86400 * checkWeekendInRange(new Date(), 3) * 1000;
      let highlightedDays = [];
      const HIGHLIGHTED_DAYS_FORMAT = 'DD-MM-YYYY';

      const isSameClinic = this.currentClinicApiData && this.currentClinicApiData.region_type === 'same_clinic';

      // --- Dynamic delivery blackouts (donor vendor + treatment country) ---
      // Donor-vendor closures: collect the vendors of the donor line(s)
      // currently in the cart and resolve their blackout ranges. Uses the
      // cached custom cart when available (donor vendors don't change on a
      // clinic switch) and degrades gracefully if the cart can't be read.
      let vendorBlackoutRanges = [];
      let expressShippingBlocked = false;
      let vendorExtraLeadTimeDays = 0;
      try {
        const customCart = await this.fetchCustomCart(false);
        const donorVendors = (customCart && customCart.items ? customCart.items : [])
          .filter(item => item.product_type === 'Donor' && item.vendor)
          .map(item => item.vendor);
        vendorBlackoutRanges = getVendorBlackoutRanges(donorVendors);
        expressShippingBlocked = donorVendors.some(isExpressBlockedVendor);
        vendorExtraLeadTimeDays = getVendorExtraLeadTimeDays(donorVendors);
      } catch (error) {
        console.error('Failed to resolve donor-vendor delivery blackouts', error);
      }

      // Australia: block the next AU_BLOCKED_DAYS_AHEAD calendar days outright
      // (earliest selectable day is the day after the cutoff).
      const isAuTreatment = this.getTreatmentSelection().country === 'AU';
      let auBlockCutoff = null;
      if (isAuTreatment) {
        auBlockCutoff = blackoutDayStart(new Date());
        auBlockCutoff.setDate(auBlockCutoff.getDate() + AU_BLOCKED_DAYS_AHEAD);
      }

      // Discount delivery is disabled for same_clinic
      let discountDeliveryEnabled = this.currentClinicApiData && (this.currentClinicApiData.discount_early_delivery === true || this.currentClinicApiData.discount_early_delivery === 'true');
      if (isSameClinic) {
        discountDeliveryEnabled = false;
        minDate = (new Date()).getTime() + 86400 * checkWeekendInRange(new Date(), 1) * 1000;
      }

      // Vendors needing extra dispatch lead time (e.g. Juno IVF): push the
      // earliest selectable date out by N calendar days on top of the
      // standard minimum. Applied after the same-clinic adjustment so it
      // stacks on whichever base minimum is in effect.
      if (vendorExtraLeadTimeDays > 0) {
        minDate += vendorExtraLeadTimeDays * 86400 * 1000;
      }

      // Show/hide express shipping hint text
      const expressHint = this.querySelector('.express-shipping-hint');
      if (expressHint) {
        expressHint.classList.toggle('hidden', isSameClinic || expressShippingBlocked);
      }

      // Day ranges for highlighting
      const expressShippingStart = moment().add(checkWeekendInRange(new Date(), 3), 'days');
      const expressShippingEnd = moment().add(7, 'days');

      // Express-window bounds as local-midnight Dates, for locking the
      // window when the cart holds an express-blocked vendor's donor.
      const expressLockStart = blackoutDayStart(expressShippingStart.toDate());
      const expressLockEnd = blackoutDayStart(expressShippingEnd.toDate());

      const earlyBirdPromptStart = moment().add(8, 'days');
      const earlyBirdPromptEnd = moment().add(30, 'days');

      const discountDeliveryStart = moment().add(31, 'days');
      const discountDeliveryEnd = moment().add(365, 'days');

      // Single source of truth for which days are blocked, shared by the
      // datepicker's lock filter and the "first selectable day" lookup below
      // so the two can never disagree.
      const isDayLocked = (day) => {
        // Block weekends (always).
        if ([6, 0].includes(day.getDay())) {
          return true;
        }
        // Australia: block the next 30 days.
        if (auBlockCutoff && blackoutDayStart(day) <= auBlockCutoff) {
          return true;
        }
        // Donor-vendor summer closures (Summer 2026).
        if (isWithinBlackoutRange(day, vendorBlackoutRanges)) {
          return true;
        }
        // Express-blocked vendor (e.g. Juno IVF): lock the express-shipping
        // window (days 3-7) so a date that would require the ExpressShipping
        // product can never be selected. Same-clinic has no express concept.
        if (expressShippingBlocked && !isSameClinic) {
          const currentDay = blackoutDayStart(day);
          if (currentDay >= expressLockStart && currentDay <= expressLockEnd) {
            return true;
          }
        }
        // Winter holiday closure 19.12.2025 - 08.01.2026 (external clinics only).
        if (!isSameClinic) {
          const holidayStart = parseBlackoutDate('2025-12-19');
          const holidayEnd = parseBlackoutDate('2026-01-08');
          const currentDay = blackoutDayStart(day);
          if (currentDay >= holidayStart && currentDay <= holidayEnd) {
            return true;
          }
        }
        return false;
      };

      // First selectable day at/after minDate, skipping weekends and every
      // blackout. Used to open the calendar on a month that actually has
      // pickable days instead of a wall of disabled ones (e.g. the AU
      // 30-day block, which can hide the whole current month).
      const findFirstSelectableDate = () => {
        const candidate = blackoutDayStart(new Date(minDate));
        for (let i = 0; i < 730; i++) {
          if (!isDayLocked(candidate)) {
            return new Date(candidate);
          }
          candidate.setDate(candidate.getDate() + 1);
        }
        return null;
      };

      if (this.deliveryDatePicker) {
        this.deliveryDatePicker.destroy();
      }

      this.deliveryDatePicker = new Litepicker({
        element: delivery,
        minDate: minDate,
        lockDaysFilter: isDayLocked,
        highlightedDays: highlightedDays,
        format: 'DD-MM-YYYY',
        setup: picker => {
          picker.on('clear:selection', () => {
            delivery.dispatchEvent(new Event('selectedDate'));
          });
          picker.on('selected', () => {
            delivery.dispatchEvent(new Event('selectedDate'));
          });
          picker.on('render:day', (dayElement, date) => {
            const day = moment(date.dateInstance);
            const isWeekend = day.day() === 0 || day.day() === 6;

            // Days 3-7: Express shipping (orange) - not for express-blocked vendors
            const isExpressShipping = !isSameClinic && !expressShippingBlocked && day.isBetween(expressShippingStart, expressShippingEnd, 'day', '[]');

            // Days 8-30: Early bird prompt (yellow) - only for early bird enabled clinics
            const isEarlyBirdPrompt = discountDeliveryEnabled && !isWeekend && day.isBetween(earlyBirdPromptStart, earlyBirdPromptEnd, 'day', '[]');

            // Days 31+: Discount delivery (green) - only for early bird enabled clinics
            const isDiscountDelivery = discountDeliveryEnabled && !isWeekend && day.isBetween(discountDeliveryStart, discountDeliveryEnd, 'day', '[]');

            if (isExpressShipping) {
              dayElement.classList.add('highlight-express-delivery');
              tippy(dayElement, {
                content: `<div style="text-align:center;">${window.cartStrings.express_shipping}</div>`,
                allowHTML: true,
                interactive: false,
                appendTo: () => document.body,
              });
            }

            if (isEarlyBirdPrompt) {
              dayElement.classList.add('highlight-early-bird-prompt');
              tippy(dayElement, {
                content: `<div style="text-align:center;">${window.cartStrings.early_bird_prompt}</div>`,
                allowHTML: true,
                interactive: false,
                appendTo: () => document.body,
              });
            }

            if (isDiscountDelivery) {
              dayElement.classList.add('highlight-discount-delivery');
              tippy(dayElement, {
                content: `<div style="text-align:center;">${window.cartStrings.delivery_discount}</div>`,
                allowHTML: true,
                interactive: false,
                appendTo: () => document.body,
              });
            }
          });
        }
      });

      // Open the calendar on the first month with a selectable day, so users
      // don't land on an all-blocked month. Skipped when a delivery date is
      // already chosen so we keep showing the user their existing selection.
      if (!delivery.value) {
        const firstSelectable = findFirstSelectableDate();
        if (firstSelectable) {
          this.deliveryDatePicker.gotoDate(firstSelectable);
        }
      }

      this.onSelectedDate = async () => {
        let isEarlyBird = false;
        let isExpressDate = false;

        if (discountDeliveryEnabled && delivery.value) {
          const date = moment(delivery.value, 'DD-MM-YYYY');

          if (date.isBetween(
            discountDeliveryStart,
            discountDeliveryEnd,
            'day',
            '[]'
          )) {
            isEarlyBird = true;
          }
        }

        if (!isSameClinic && !expressShippingBlocked && delivery.value) {
          const date = moment(delivery.value, 'DD-MM-YYYY');
          if (date.isBetween(expressShippingStart, expressShippingEnd, 'day', '[]')) {
            isExpressDate = true;
          }
        }

        // Express shipping confirmation
        const expressProduct = window.theme && window.theme.expressShippingVariant;
        if (isExpressDate && expressProduct) {
          const alreadyInCart = await this.isExpressShippingInCart();
          if (!alreadyInCart) {
            const confirmed = await this.showExpressShippingConfirmation();
            if (!confirmed) {
              this.deliveryDatePicker.clearSelection();
              delivery.value = '';
              return;
            }
          }
        }

        this.querySelectorAll('.product-add-variants-item-price').forEach(div => {
          const originalPrice = parseInt(div.querySelector('.product-add-variants-item-price--original').dataset.price);
          const isDiscounted = originalPrice > 0 && isEarlyBird;
          const discount = 0.5; // 50%

          if (isDiscounted) {
            div.classList.add('discount');
            div.querySelector('.product-add-variants-item-price--current').innerHTML = app.moneyWithCurrency(originalPrice / 100 * discount);
          } else {
            div.classList.remove('discount');
          }
        });

        this.startLoadingClinic('deliver-date');

        fetch(window.Shopify.routes.root + "cart/update.js", {
          ...fetchConfig("javascript"),
          body: JSON.stringify({
            attributes: {
              delivery_till: delivery.value
            }
          })
        }).then(() => {
          this.fetchCart().then(cart => {
            let update = false;
            let data = null;

            cart.items.forEach(item => {
              if (item.product_type !== 'Clinic') {
                return;
              }

              if (!isEarlyBird && item.properties && item.properties.__early_bird === 'true') {
                data = {
                  id: item.key,
                  properties: {
                    ...item.properties,
                    __early_bird: ''
                  },
                  quantity: item.quantity
                }
                update = true;
              } else if (isEarlyBird && (!item.properties || item.properties.__early_bird !== 'true')) {
                data = {
                  id: item.key,
                  properties: {
                    ...item.properties,
                    __early_bird: 'true'
                  },
                  quantity: item.quantity
                }
                update = true;
              }
            });

            // Express shipping product management
            const expressShippingManaged = this.manageExpressShipping(cart, isExpressDate);

            if (!update && !expressShippingManaged) {
              this.stopLoadingClinic('deliver-date');
              this.update();

              return;
            }

            if (!update && expressShippingManaged) {
              return;
            }

            this.changeCartItems([data])
              .then(res => {
                this.fetchCartSection('order-summary').finally(() => {
                  this.stopLoadingClinic('deliver-date')
                });
                this.update();
              })
              .catch(error => {
                console.error(error);
                this.stopLoadingClinic('deliver-date');
                this.update();
              });
          }).catch(error => {
            console.error(error);
            this.stopLoadingClinic('deliver-date');
            this.update();
          });
        })
      }

      if (!delivery.classList.contains('selected-date-initialized')) {
        delivery.addEventListener('selectedDate', (e) =>{
          this.onSelectedDate(e);
        });
        delivery.classList.add('selected-date-initialized');
      }
    }

    initTreatmentDate ()
    {
      const input = this.querySelector('#person-treatment-date');
      const unknownCheckbox = this.querySelector('#cart-person-treatment-date-unknown');
      const warningEl = this.querySelector('.cart-person-treatment-date-warning');

      if (!input) {
        return;
      }

      // Initialize Litepicker with future dates only
      if (this.treatmentDatePicker) {
        this.treatmentDatePicker.destroy();
      }

      this.treatmentDatePicker = new Litepicker({
        element: input,
        format: 'DD-MM-YYYY',
        minDate: new Date(),
        setup: (picker) => {
          picker.on('selected', () => {
            this.validateTreatmentDate();
            this.validate();
          });
        }
      });

      // Toggle date picker disabled when "I don't know" is checked
      if (unknownCheckbox) {
        unknownCheckbox.addEventListener('change', () => {
          input.disabled = unknownCheckbox.checked;
          if (unknownCheckbox.checked) {
            input.value = '';
            if (warningEl) {
              warningEl.classList.add('hidden');
            }
          }
          this.validate();
        });

        // Set initial state
        if (unknownCheckbox.checked) {
          input.disabled = true;
        }
      }
    }

    validateTreatmentDate ()
    {
      const treatmentInput = this.querySelector('#person-treatment-date');
      const warningEl = this.querySelector('.cart-person-treatment-date-warning');
      const deliveryTill = this.querySelector('[name="delivery_till"]')?.value;

      if (!treatmentInput?.value || !deliveryTill || !warningEl) {
        if (warningEl) {
          warningEl.classList.add('hidden');
        }
        return;
      }

      // Parse dates (DD-MM-YYYY format)
      const parseDate = (str) => {
        const parts = str.split('-');
        if (parts.length !== 3) return null;
        const [d, m, y] = parts.map(Number);
        return new Date(y, m - 1, d);
      };

      const treatmentDate = parseDate(treatmentInput.value);
      const deliveryDate = parseDate(deliveryTill);

      if (treatmentDate && deliveryDate && treatmentDate < deliveryDate) {
        warningEl.classList.remove('hidden');
      } else {
        warningEl.classList.add('hidden');
      }
    }

    initAfterSectionsRender ()
    {
      this.initDivs();
      this.initLogic();
      this.update();
    }

    initClinics ()
    {
      if (!this.clinicInput) {
        this.debugLog(`[CLINIC-DEBUG] initClinics: No clinic input found`);
        return;
      }
      const currentClinic = this.querySelector('input[name="clinic_selected"]');

      this.debugLog(`[CLINIC-DEBUG] ========== INIT CLINICS START ==========`);
      this.debugLog(`[CLINIC-DEBUG] Current clinic input value: ${this.clinicInput.value}`);
      this.debugLog(`[CLINIC-DEBUG] Current clinic hidden value: ${currentClinic ? currentClinic.value : 'NOT FOUND'}`);
      this.debugLog(`[CLINIC-DEBUG] Current clinic ID state: ${this.currentClinicId}`);

      this.startLoadingClinic('initClinics');

      // No treatment country means the clinics endpoint has nothing to filter
      // on — the list would come back empty. Ask the customer to choose one
      // via the same popup the catalogue uses; the treatment_country_change
      // broadcast then reloads the list.
      if (!this.getTreatmentSelection().country) {
        const treatmentPopup = document.getElementById('popup-treatment');

        if (treatmentPopup && typeof(treatmentPopup.open) === 'function'
          && (typeof(treatmentPopup.isOpen) !== 'function' || !treatmentPopup.isOpen())
        ) {
          this.debugLog(`[CLINIC-DEBUG] No treatment country set, opening treatment popup`);
          treatmentPopup.classList.remove('has-country');
          treatmentPopup.open();
        }
      }

      if (!currentClinic || !currentClinic.value || !this.clinicInput) {
        this.debugLog(`[CLINIC-DEBUG] No current clinic value found, loading all clinics`);
        if (this.querySelector('.product-add-variants-fieldset')) {
          this.querySelector('.product-add-variants-fieldset').classList.add('hidden');
        }

        this.loadClinics().then(() => {
          this.update();
          this.stopLoadingClinic('initClinics');
          this.debugLog(`[CLINIC-DEBUG] ========== INIT CLINICS END (no selection) ==========`);
        }).catch(error => {
          console.error('[CLINIC-DEBUG][ERROR] Failed to load clinics:', error);
          this.stopLoadingClinic('initClinics');
        });
        return;
      }

      const data = currentClinic.value.split(":");
      this.debugLog(`[CLINIC-DEBUG] Parsed clinic data: ${data.join(', ')}`);
      this.loadClinics().then(() => {
        this.debugLog(`[CLINIC-DEBUG] Setting clinic input value to: ${data[0]}`);
        this.clinicInput.value = data[0];

        // Don't sync state here - let changeClinic() handle it after fetching API data
        // This ensures the early bird discount flag is fetched on first page load

        if (this.clinicInput.value === data[0]) {
          this.debugLog(`[CLINIC-DEBUG] Dispatching change event for clinic: ${data[0]}`);
          this.clinicInput.dispatchEvent(new Event("change"));
        } else {
          // The saved clinic is not among the loaded options (different
          // country, list failed to load, ...) — dispatching change here
          // would run changeClinic('') against a garbage URL, so keep the
          // placeholder selected instead.
          this.debugLog(`[CLINIC-DEBUG][WARNING] Saved clinic ${data[0]} not in loaded list, keeping placeholder`);
        }

        this.stopLoadingClinic('initClinics');
        this.debugLog(`[CLINIC-DEBUG] ========== INIT CLINICS END ==========`);
      }).catch(error => {
        console.error('[CLINIC-DEBUG][ERROR] Failed to load clinics:', error);
        this.stopLoadingClinic('initClinics');
      });
    }

    initBanks ()
    {
      const currentBank = this.querySelector('input[name="bank_selected"]');

      if (!currentBank || !currentBank.value) {
        if (this.bankInput) {
          this.bankInput.dispatchEvent(new Event("change"));
        }

        this.hideBanks();

        return;
      }

      const data = currentBank.value.split(":");
      this.bankInput.value = data[0];
      const select = this.querySelector(`.bank-storage-time[data-bank-id="${data[0]}"] select`);
      select.value = currentBank.value;

      this.bankInput.dispatchEvent(new Event("change"));
      select.dispatchEvent(new Event("change"));

      this.hideBanks();
    }

    hideBanks ()
    {
      if (!this.bankInput) {
        return;
      }

      const select = this.bankInput.closest('custom-select');

      this.fetchCustomCart().then(response => {
        if (!response.items || response.items.length === 0) {
          return response;
        }

        let bankId = null;

        response.items.forEach(item => {
          if (item.properties
            && item.properties.__shipping === 'bank'
            && item.metafields
            && typeof(item.metafields['donor.donor_bank']) !== 'undefined'
            && item.metafields['donor.donor_bank'].id
          ) {
            bankId = item.metafields['donor.donor_bank'].id + '';
          }
        });

        select.filter(option => {
          if (!bankId || !option.value) {
            return true;
          }

          return ('' + option.value).toLowerCase() === bankId.toLowerCase();
        });

        return response;
      });
    }

    getDonorCountry ()
    {
      if (this.clinicInput) {
        return this.clinicInput.closest('custom-select').dataset.donorClinicCountry;
      }

      return null;
    }

    getDonorClinicShopifyId ()
    {
      if (this.clinicInput) {
        return this.clinicInput.closest('custom-select').dataset.donorClinicId;
      }

      return null;
    }


    // Thin, null-safe wrapper around the single resolver on TreatmentCountry
    // (works before app.init() has run — see getSelection() there).
    getTreatmentSelection ()
    {
      if (window.app && window.app.treatmentCountry) {
        return window.app.treatmentCountry.getSelection();
      }

      return { country: null, state: null };
    }

    // Fetch with a hard timeout: converts a hung connection into a rejection
    // so the callers' catch/finally paths run (loader released, execution
    // lock freed) instead of spinning forever on a request that never
    // settles.
    fetchWithTimeout (url, options = {}, timeoutMs = 12000)
    {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), timeoutMs);

      return fetch(url, { ...options, signal: controller.signal })
        .finally(() => clearTimeout(timer));
    }

    // One automatic retry on top of the timeout: a single transient blip
    // (dropped connection, gateway hiccup, timeout) self-heals instead of
    // leaving the customer with an empty dropdown until they reload.
    async fetchClinicList (clinicsUrl)
    {
      for (let attempt = 1; ; attempt++) {
        try {
          const response = await this.fetchWithTimeout(clinicsUrl);

          if (!response.ok) {
            throw new Error('Clinics request failed with status ' + response.status);
          }

          const clinicsResponse = await response.json();

          return Array.isArray(clinicsResponse.data) ? clinicsResponse.data : [];
        } catch (error) {
          if (attempt >= 2) {
            throw error;
          }

          this.debugLog(`[CLINIC-DEBUG][WARNING] Clinics request failed (attempt ${attempt}), retrying:`, error);
        }
      }
    }

    async loadClinics ()
    {
      const selection = this.getTreatmentSelection();
      const treatmentCountryCode = selection.country;
      const treatmentState = selection.state;

      if (!treatmentCountryCode) {
        // Without a country the endpoint would be called with
        // country_code=undefined and return nothing — bail out loudly instead
        // of rendering a silently empty dropdown.
        this.debugLog('[CLINIC-DEBUG][ERROR] No treatment country available, clinic list not loaded');
        return [];
      }

      let clinicsUrl = "https://app.donornetwork.com/api/shopify/clinics?country_code=" + treatmentCountryCode;
      // When an AU state is selected, narrow the clinics to that state's market
      // (e.g. AU-NSW) server-side. Only send `state` for AU: the value can be
      // stale (e.g. AU-QLD left over after switching country to DK), and the
      // backend rejects a state that doesn't belong to the country, returning
      // zero clinics.
      if (treatmentCountryCode === 'AU' && treatmentState) {
        clinicsUrl += "&state=" + encodeURIComponent(treatmentState);
      }

      // Only the newest request may render. loadClinics can run concurrently
      // (initial load + country-change reload); without this, a slow response
      // for the previous country would land last and overwrite the list.
      const requestId = ++this.clinicsRequestId;

      const clinics = await this.fetchClinicList(clinicsUrl);

      if (requestId !== this.clinicsRequestId) {
        this.debugLog(`[CLINIC-DEBUG] Discarding stale clinics response (request ${requestId} of ${this.clinicsRequestId})`);
        return clinics;
      }

      // Rebuild instead of append: initClinics runs again after section
      // re-renders, and appending on top of the old list duplicates options.
      this.clinicInput.querySelectorAll('option:not([data-option-placeholder])').forEach(option => option.remove());

      clinics.forEach(clinic => {
        const option = document.createElement('option');
        option.value = clinic.id;
        option.dataset.country = clinic.country_code;
        option.innerText = clinic.name;
        this.clinicInput.appendChild(option);
      });
      this.clinicInput.dispatchEvent(new Event('modified'));
      return clinics;
    }

    async changeClinic ()
    {
      const startTime = performance.now();
      const currentTime = Date.now();
      const destinationID = this.clinicInput.value;

      this.debugLog(`[CLINIC-DEBUG] ========== CHANGE CLINIC START ==========`);
      this.debugLog(`[CLINIC-DEBUG] Destination ID: ${destinationID}`);
      this.debugLog(`[CLINIC-DEBUG] Current clinic ID: ${this.currentClinicId}`);
      this.debugLog(`[CLINIC-DEBUG] Current time: ${currentTime}, Last change time: ${this.lastClinicChangeTime}`);

      if (this.shipmentSettings.classList.contains("disabled")) {
        this.debugLog('[CLINIC-DEBUG][SKIP] Shipment settings disabled');
        return;
      }

      // Early exit if already changing clinic (execution lock)
      if (this.isChangingClinic) {
        this.debugLog('[CLINIC-DEBUG][SKIP] Already changing clinic, ignoring duplicate call');
        return;
      }

      // Debouncing: prevent rapid successive calls within 200ms
      if (currentTime - this.lastClinicChangeTime < 200) {
        this.debugLog(`[CLINIC-DEBUG][SKIP] Debounced: too soon after last change (${currentTime - this.lastClinicChangeTime}ms)`);
        return;
      }

      // Early exit if same clinic already selected
      if (this.currentClinicId === destinationID) {
        this.debugLog(`[CLINIC-DEBUG][SKIP] Same clinic already selected: ${destinationID}`);
        return;
      }

      // Hide address hint while changing
      const addressHintOnChange = this.querySelector('.clinic-address-hint');
      if (addressHintOnChange) addressHintOnChange.classList.add('hidden');

      // Set execution lock and update state
      this.isChangingClinic = true;
      this.lastClinicChangeTime = currentTime;
      this.debugLog(`[CLINIC-DEBUG][EXECUTE] Changing clinic from ${this.currentClinicId} to ${destinationID}`);

      try {
        this.startLoadingClinic('changeClinic');

      // Cache DOM elements for better performance
      const domStart = performance.now();
      const cartNotification = document.querySelector("cart-notification") || document.querySelector("cart-drawer");
      const cart = document.querySelector("cart-items");
      const currentClinic = this.querySelector('input[name="clinic_selected"]');
      const variantsFieldset = this.querySelector(".product-add-variants-fieldset");
      const variantsContainer = this.querySelector('.product-add-variants-items-container');
      this.debugLog(`[CLINIC-DEBUG] DOM elements cached in ${performance.now() - domStart}ms`);
      this.debugLog(`[CLINIC-DEBUG] Current clinic input value: ${currentClinic ? currentClinic.value : 'NOT FOUND'}`);

      // Parallelize API calls for better performance
      const parallelStart = performance.now();
      // Timeout so a hung backend call rejects and the finally below releases
      // the execution lock — otherwise the dropdown deadlocks for the session.
      const [destinationResponse, currentCart] = await Promise.all([
        this.fetchWithTimeout("https://app.donornetwork.com/api/shopify/clinics/"+destinationID+"?alias=" + this.donorNameInput.value)
          .then(response => response.json()),
        this.fetchCart()
      ]);
      this.debugLog(`[CLINIC-DEBUG] API calls completed in ${performance.now() - parallelStart}ms`);
      this.debugLog(`[CLINIC-DEBUG] Destination response status: ${destinationResponse ? 'SUCCESS' : 'FAILED'}`);
      this.debugLog(`[CLINIC-DEBUG] Current cart items count: ${currentCart ? currentCart.items.length : 'UNKNOWN'}`);

      if (destinationResponse && destinationResponse.data) {
        this.debugLog(`[CLINIC-DEBUG] Destination response keys: ${Object.keys(destinationResponse.data).join(', ')}`);
        if (destinationResponse.data.shipment_price) {
          this.debugLog(`[CLINIC-DEBUG] Shipment price keys: ${Object.keys(destinationResponse.data.shipment_price).join(', ')}`);
        }
      }

      const destination = destinationResponse.data;

      this.currentClinicApiData = destination;

      if (!destination) {
        this.debugLog(`[CLINIC-DEBUG][ERROR] No destination data received`);
        return;
      }

      // Reinitialize delivery date picker with new clinic data (for discount_early_delivery flag)
      this.initDeliveryDate();

      this.debugLog(`[CLINIC-DEBUG] Destination data: ${JSON.stringify(destination)}`);

      if (!destination.shipment_price) {
        this.debugLog(`[CLINIC-DEBUG][ERROR] No shipment_price data in destination`);
        this.debugLog(`[CLINIC-DEBUG] Available destination properties: ${Object.keys(destination).join(', ')}`);
        return;
      }

      if (!destination.shipment_price.external_id) {
        this.debugLog(`[CLINIC-DEBUG][ERROR] No external_id in shipment_price`);
        this.debugLog(`[CLINIC-DEBUG] Shipment price data: ${JSON.stringify(destination.shipment_price)}`);
        return;
      }

      const variantStart = performance.now();
      const variantId = this.extractVariantId(destination.shipment_price.external_id);

      // Fetch variant data and clinic address in parallel
      const [variant, clinicAddressHtml] = await Promise.all([
        this.getVariantByExternalId(destination.shipment_price.external_id),
        fetch(`${window.Shopify.routes.root}variants/${variantId}/?section_id=clinic-address-lookup`)
          .then(r => r.text())
          .catch(() => null)
      ]);
      this.debugLog(`[CLINIC-DEBUG] Variant fetch completed in ${performance.now() - variantStart}ms`);
      this.debugLog(`[CLINIC-DEBUG] Variant found: ${variant ? variant.id : 'NOT FOUND'}`);

      if (!variant) {
        this.debugLog(`[CLINIC-DEBUG][ERROR] No variant found for external_id: ${destination.shipment_price.external_id}`);
        return;
      }

      // Show clinic address hint from section rendering response
      this.debugLog(`[CLINIC-DEBUG] Address HTML response: ${clinicAddressHtml ? clinicAddressHtml.substring(0, 500) : 'NULL'}`);
      const addressHint = this.querySelector('.clinic-address-hint');
      if (addressHint && clinicAddressHtml) {
        const doc = new DOMParser().parseFromString(clinicAddressHtml, 'text/html');
        const addressEl = doc.querySelector('.clinic-address-data');
        const address = addressEl ? addressEl.textContent.trim() : null;
        if (address) {
          addressHint.textContent = address;
          addressHint.classList.remove('hidden');
        } else {
          addressHint.classList.add('hidden');
        }
      } else if (addressHint) {
        addressHint.classList.add('hidden');
      }

      this.debugLog(`[CLINIC-DEBUG] Updating DOM elements for new clinic selection`);
      variantsFieldset.classList.remove("hidden");
      if (variantsContainer.querySelectorAll('.product-add-variants-items').length > 0) {
        variantsContainer.innerHTML = '';
      }
      const items = document.createElement('div');
      items.classList.add('product-add-variants-items');

      const label = document.createElement('label');
      label.classList.add('product-add-variants-item');
      label.for = `clinic-shipment`;
      label.innerHTML = `
          <input
            type="radio"
            id="clinic-shipment"
            name="${destination.shipment_price.region_type}"
            checked
          >
          <div class="product-add-variants-item-status"></div>
          <div class="product-add-variants-item-name">${variant.public_title}</div>
          <div class="product-add-variants-item-price">
            <span class="product-add-variants-item-price--original" data-price="${variant.price}">${app.moneyWithCurrency(variant.price / 100)}</span>
            <span class="product-add-variants-item-price--current"></span>
          </div>
        `;

      items.appendChild(label);
      variantsContainer.appendChild(items);

      const expectedClinicValue = destination.id+":"+variant.id;
      this.debugLog(`[CLINIC-DEBUG] Expected clinic value: ${expectedClinicValue}`);
      this.debugLog(`[CLINIC-DEBUG] Current clinic value: ${currentClinic ? currentClinic.value : 'NOT FOUND'}`);

      if (currentClinic && currentClinic.value === expectedClinicValue) {
        this.debugLog(`[CLINIC-DEBUG] Clinic already set to correct value, skipping cart update`);
        this.stopLoadingClinic('changeClinic');
        this.currentClinicId = destinationID;
        this.isChangingClinic = false;
        this.update();
        return;
      }

      // Combine remove + add operations into single atomic cart update
      const atomicStart = performance.now();
      const itemsToRemove = [];

      currentCart.items.forEach(item => {
        if (item.product_type == 'Clinic') {
          itemsToRemove.push(item.id);
        }
      });

      this.debugLog(`[CLINIC-DEBUG] Found ${itemsToRemove.length} clinic items to remove`);
      this.debugLog(`[CLINIC-DEBUG] Items to remove: ${itemsToRemove.join(', ')}`);

      // Prepare atomic cart update: remove old clinics + add new clinic
      const updates = {};
      itemsToRemove.forEach(itemId => {
        updates[itemId] = 0; // Remove existing clinic items
      });

      const addData = {
        items: [
          {
            id: parseInt(variant.id),
            properties: {
              __destination: destination.id,
            },
            quantity: 1,
          }
        ]
      };

      this.debugLog(`[CLINIC-DEBUG] Adding new clinic item: ${variant.id} with destination: ${destination.id}`);

      if (itemsToRemove.length > 0) {
        this.debugLog(`[CLINIC-DEBUG] Removing old clinic items first`);
        await fetch(window.Shopify.routes.root + "cart/update.js", {
          ...fetchConfig("javascript"),
          body: JSON.stringify({ updates: updates })
        });
      }

      // Perform atomic operation: add new item and remove old ones simultaneously
      const [addResponse] = await Promise.all([
        fetch(window.Shopify.routes.root + "cart/add.js?" + app.getQueryParamsForCartSectionsRender(), {
          ...fetchConfig("javascript"),
          body: JSON.stringify(addData)
        }),
      ]);
      this.debugLog(`[CLINIC-DEBUG] Cart operations completed in ${performance.now() - atomicStart}ms`);
      this.debugLog(`[CLINIC-DEBUG] Add response status: ${addResponse.status}`);

      const updateStart = performance.now();
      this.debugLog(`[CLINIC-DEBUG] Starting clinic properties update`);
      await this.updateCurrentClinicProperties(async () => {
        const checkStart = performance.now();
        await this.checkCart();
        this.debugLog(`[CLINIC-DEBUG] checkCart completed in ${performance.now() - checkStart}ms`);
      });
      this.debugLog(`[CLINIC-DEBUG] updateCurrentClinicProperties completed in ${performance.now() - updateStart}ms`);

      const result = await addResponse.json();
      this.debugLog(`[CLINIC-DEBUG] Cart add result status: ${result.status || 'SUCCESS'}`);
      this.debugLog(`[CLINIC-DEBUG] Cart add result description: ${result.description || 'NONE'}`);

      if (result.status) {
        console.error('[CLINIC-DEBUG][ERROR] Failed to add to cart:', result.description);

        // The old clinic line (if any) was already removed, so the cart now has
        // no clinic line. Clear the marker so validate() keeps the step gated.
        if (currentClinic) {
          currentClinic.value = '';
        }

        this.validate();

        return;
      }

      const newClinicValue = destination.id+":"+variant.id;
      this.debugLog(`[CLINIC-DEBUG] Setting currentClinic value to: ${newClinicValue}`);

      // Only mark the clinic as selected once the line is confirmed in the cart
      if (currentClinic) {
        currentClinic.value = newClinicValue;
        this.debugLog(`[CLINIC-DEBUG] Updated hidden input field: ${currentClinic.value}`);
      } else {
        this.debugLog(`[CLINIC-DEBUG][WARNING] Current clinic input not found`);
      }

      if (!cartNotification) {
        this.debugLog(`[CLINIC-DEBUG] No cart notification found, redirecting to cart URL`);
        window.location = window.routes.cart_url;
        return;
      }

      this.debugLog(`[CLINIC-DEBUG] Rendering cart contents with ${result.sections ? 'sections' : 'no sections'}`);
      try {
        cartNotification.renderContents({
          items: [],
          sections: result.sections
        }, true);
        cart.renderContents(result);
      } catch (e) {
        console.error('[CLINIC-DEBUG][ERROR] Failed to render cart contents:', e);
      }

      const renderStart = performance.now();
      this.initAfterSectionsRender();
      this.debugLog(`[CLINIC-DEBUG] initAfterSectionsRender completed in ${performance.now() - renderStart}ms`);

      // Update state and release execution lock
      this.currentClinicId = destinationID;
      this.isChangingClinic = false;

      // Ensure state consistency
      this.syncClinicState();

      this.debugLog(`[CLINIC-DEBUG] ========== CHANGE CLINIC END ==========`);
      this.debugLog(`[CLINIC-DEBUG] Final state - currentClinicId: ${this.currentClinicId}, isChangingClinic: ${this.isChangingClinic}`);
      this.debugLog(`[CLINIC-DEBUG] Total changeClinic took ${performance.now() - startTime}ms`);

      this.stopLoadingClinic('changeClinic');
      
      } catch (error) {
        console.error('[CLINIC-DEBUG][ERROR] Failed to change clinic:', error);
        console.error('[CLINIC-DEBUG][ERROR] Error details:', {
          message: error.message,
          stack: error.stack,
          destinationID: destinationID,
          currentClinicId: this.currentClinicId
        });
        throw error;
      } finally {
        // Release the lock on EVERY exit — the early `return`s above (no
        // destination, no shipment_price, no variant, cart add rejected) used
        // to leave isChangingClinic=true and the loader spinning forever,
        // deadlocking the dropdown until a full page reload.
        this.isChangingClinic = false;
        this.stopLoadingClinic('changeClinic');
      }
    }

    changeBank ()
    {
      if (this.storageSettings.classList.contains("disabled")) {
        return;
      }

      const cartNotification = document.querySelector("cart-notification") || document.querySelector("cart-drawer");
      const cart = document.querySelector("cart-items");
      const data = {
        items: []
      };
      const selectedBankId = this.querySelector(`.bank-storage-time[data-bank-id="${this.bankInput.value}"] select`).value;
      const currentBank = this.querySelector('input[name="bank_selected"]');
      const currentBankId = currentBank.value;

      if (selectedBankId === currentBankId) {
        return;
      }

      data.items.push({
        id: selectedBankId.split(":")[1],
        quantity: 1
      });

      currentBank.value = selectedBankId;

      this.bankLoaderDiv.classList.remove("hidden");
      this.querySelector(".bank-storage-banks").classList.add("hidden");
      this.querySelector(".bank-storage-times").classList.add("hidden");

      const addCurrentBank = () => {
        fetch(window.Shopify.routes.root + "cart/add.js?" + app.getQueryParamsForCartSectionsRender(), {
          ...fetchConfig("javascript"),
          body: JSON.stringify(data)
        }).then(response => response.json())
          .then(response => {
            this.bankErrorDiv.classList.add("hidden");

            if (response.status) {
              this.bankErrorDiv.textContent = response.description;
              this.bankErrorDiv.classList.remove("hidden");

              return;
            }

            this.querySelector(".bank-storage-banks").classList.remove("hidden");
            this.querySelector(".bank-storage-times").classList.remove("hidden");
            this.updateCurrentBankProperties();

            if (!cartNotification) {
              window.location = window.routes.cart_url;

              return;
            }

            cartNotification.renderContents({
              items: [],
              sections: response.sections
            }, true);
            cart.renderContents(response);
            this.initAfterSectionsRender();
          }).catch(e => {
            console.error(e);
          }).finally(() => {
            if (cart && cart.classList.contains("is-empty")) {
              cart.classList.remove("is-empty");
            }

            this.bankLoaderDiv.classList.add("hidden");
            this.initBanks();
          })
      };

      if (!selectedBankId) {
        this.removeCurrentBanks(() => {
          fetch(window.Shopify.routes.root + "?" + app.getQueryParamsForCartSectionsRender())
            .then(response => response.json())
            .then(response => {
              this.bankLoaderDiv.classList.add("hidden");
              cart.renderContents({
                sections: response
              });
              this.initAfterSectionsRender();
              this.updateCurrentBankProperties();
            })
        });

        return;
      }

      if (currentBankId) {
        this.removeCurrentBanks(() => {
          addCurrentBank()
        });
      } else {
        addCurrentBank();
      }
    }

    removeCurrentShipments (callback)
    {
      this.startLoadingClinic('removeCurrentShipments');
      this.removeByProductType('Clinic', ['__treatment_target_local_id'], response => {
        if (typeof(callback) === 'function') {
          callback(response);
        }
        this.stopLoadingClinic('removeCurrentShipments');
      });
    }

    removeCurrentBanks (callback)
    {
      this.removeByProductType('Bank', ['__cryogenic_storage_extension'], response => {
        if (typeof(callback) === 'function') {
          callback(response);
        }
      });
    }

    removeDonor (productId, variantId, lineItemKey, lineItemLocalId, callback)
    {
      this.fetchCustomCart()
        .then(response => {
          if (!response.items || response.items.length === 0) {
            return response;
          }

          const items = [];
          const updates = {};
          let proceed = true;

          response.items.forEach(item => {
            if (typeof(item.properties) === 'undefined' || !proceed) {
              return;
            }

            if (item.properties && item.properties.__item_local_id == lineItemLocalId) {
              items.push(item.key);
              updates[item.key] = 0;
            } else if (item.properties && item.properties.__line_item_local_id == lineItemLocalId) {
              if (item.product_type === 'PregnancyQuota') {
                let usedByOther = false;

                response.items.forEach(other => {
                  // Any remaining donor line of the same product can inherit the one
                  // shared PQ — ship, bank or reservation. (Reservations used to be
                  // excluded here, back when they didn't require a PQ.)
                  if (other.properties
                    && other.properties.__item_local_id != lineItemLocalId
                    && other.product_id == productId
                  ) {
                    usedByOther = other;
                  }
                });

                // A surviving line that is already covered by another quota
                // must not inherit a second one: the quota row has no remove
                // control, so a consolidated duplicate would be stuck in the
                // cart (and charged) until the whole donor is removed.
                if (usedByOther) {
                  const alreadyCovered = response.items.some(quota2 =>
                    quota2.product_type === 'PregnancyQuota'
                    && quota2.key !== item.key
                    && quota2.properties
                    && quota2.properties.__line_item_local_id
                    && response.items.some(donor2 => donor2.product_type === 'Donor'
                      && donor2.product_id == productId
                      && donor2.properties
                      && donor2.properties.__item_local_id != lineItemLocalId
                      && donor2.properties.__item_local_id == quota2.properties.__line_item_local_id)
                  );

                  if (alreadyCovered) {
                    usedByOther = false;
                  }
                }

                if (!usedByOther) {
                  items.push(item.key);
                  updates[item.key] = 0;
                } else {
                  console.info('Pregnancy Quota is used by other variant, switching', usedByOther, lineItemKey, productId, variantId);

                  item.properties['For'] = usedByOther.product_title;
                  item.properties.__line_item_local_id = usedByOther.properties.__item_local_id;
                  item.properties.__variant_id = usedByOther.variant_id;
                  item.properties.__line_item_id = usedByOther.id;

                  // Stamp the inheriting line when it lacks the property (e.g.
                  // a MOT 2 companion added straight from the cart) — the
                  // render and cleanup paths key off it, so an unstamped line
                  // would show no quota row and removeInvalidProducts once
                  // deleted the paid quota as "unused".
                  const stampInheritingDonor = !usedByOther.properties.__pregnancy_quota
                    ? () => {
                        usedByOther.properties.__pregnancy_quota = item.product_id + ':' + item.variant_id;

                        return this.cartChange({
                          id: usedByOther.key,
                          properties: usedByOther.properties,
                          quantity: usedByOther.quantity
                        });
                      }
                    : () => Promise.resolve();

                  // The exclusivity fee moves with its quota — left on the
                  // removed line it would be deleted with it below.
                  const repointExclusivityFees = () => response.items
                    .filter(fee => fee.product_type === 'ExclusivityFee'
                      && fee.properties
                      && fee.properties.__line_item_local_id == lineItemLocalId)
                    .reduce((chain, fee) => chain.then(() => this.cartChange({
                      id: fee.key,
                      properties: {
                        ...fee.properties,
                        'For': usedByOther.product_title,
                        __line_item_local_id: usedByOther.properties.__item_local_id,
                        __variant_id: usedByOther.variant_id,
                        __line_item_id: usedByOther.id
                      },
                      quantity: fee.quantity
                    })), Promise.resolve());

                  this.cartChange({
                    id: item.key,
                    properties: item.properties,
                    quantity: item.quantity
                  }).then(() => stampInheritingDonor())
                    .then(() => repointExclusivityFees())
                    .then(() => {
                    this.startLoadingDonor('removeDonor');
                    this.removeDonor(productId, variantId, lineItemKey, lineItemLocalId, (data) => {
                      this.stopLoadingDonor('removeDonor');

                      if (typeof(callback) === 'function') {
                        callback(data);
                      }
                    });
                  });

                  proceed = false;
                  return;
                }

              } else {
                items.push(item.key);
                updates[item.key] = 0;
              }
            }
          });

          if (items.length > 0 && proceed) {
            fetch(window.Shopify.routes.root + "cart/update.js", {
              ...fetchConfig("javascript"),
              body: JSON.stringify({
                updates: updates
              })
            }).then(response => response.json())
              .then(response => {
                if (typeof(callback) === 'function') {
                  callback(response);
                }
              });

            return response;
          }

          if (typeof(callback) === 'function' && proceed) {
            callback(response);
          }

          return response;
        });
    }

    removeByProductType (type, propertyExceptions, callback)
    {
      this.debugger.log('[Cart] Remove product by type', type, propertyExceptions);

      fetch(window.Shopify.routes.root + "cart.json", fetchConfig("javascript"))
        .then(response => response.json())
        .then(response => {
          const items = [];
          const updates = {};
          let allow = true;

          if (response.items && response.items.length) {
            response.items.forEach(item => {
              if (item.product_type === type) {
                allow = true;

                propertyExceptions.forEach(property => {
                  if (typeof(item.properties[property]) !== 'undefined') {
                    allow = false;
                  }
                });

                if (allow) {
                  items.push(item.key);
                  updates[item.key] = 0;
                }
              }
            });
          }

          if (items.length > 0) {
            fetch(window.Shopify.routes.root + "cart/update.js", {
              ...fetchConfig("javascript"),
              body: JSON.stringify({
                updates: updates
              })
            }).then(response => response.json())
              .then(response => {
                if (typeof(callback) === 'function') {
                  callback(response);
                }
              });

            return;
          }

          if (typeof(callback) === 'function') {
            callback(response);
          }
        });
    }

    updateCurrentBankProperties (callback)
    {
      fetch(window.Shopify.routes.root + "cart.json", fetchConfig("javascript"))
        .then(response => response.json())
        .then(response => {
          const updates = [];
          const selectedId = this.querySelector('[name="bank_selected"]').value;

          if (response.items && response.items.length) {
            response.items.forEach(item => {
              if (item.properties
                && item.properties.__shipping === 'bank'
                && item.properties.__cryogenic_storage !== selectedId
              ) {
                if (selectedId) {
                  item.properties.__cryogenic_storage = selectedId;
                } else if (typeof(item.properties.__cryogenic_storage) !== 'undefined') {
                  delete item.properties.__cryogenic_storage;
                }

                updates.push({
                  id: item.key,
                  properties: item.properties,
                  quantity: item.quantity
                });
              }
            });
          }

          this.changeCartItems(updates)
            .then(res => {
              if (typeof(callback) === 'function') {
                callback(res);
              }
            })
            .catch(error => {
              if (typeof(callback) === 'function') {
                callback();
              }
            });
        });
    }

    async updateCurrentClinicProperties (callback)
    {
      this.debugLog(`[CLINIC-DEBUG] ========== UPDATE CLINIC PROPERTIES START ==========`);
      this.startLoadingClinic('updateCurrentClinicProperties');

      fetch(window.Shopify.routes.root + "cart.json", fetchConfig("javascript"))
        .then(response => response.json())
        .then(response => {
          const selectedClinicValue = this.querySelector('[name="clinic_selected"]').value;
          this.debugLog(`[CLINIC-DEBUG] Selected clinic value: ${selectedClinicValue}`);
          this.debugLog(`[CLINIC-DEBUG] Cart items count: ${response.items ? response.items.length : 0}`);

          const updates = [];
          let clearDeliveryDate = false;
          let clinicHasChanged = false;

          if (response.items && response.items.length) {
            response.items.forEach(item => {
              if (item.properties && item.properties.__shipping === 'clinic') {
                const currentClinicValue = item.properties.__clinic;
                this.debugLog(`[CLINIC-DEBUG] Item ${item.key} current clinic: ${currentClinicValue}, target: ${selectedClinicValue}`);

                if (item.properties.__clinic !== selectedClinicValue) {
                  if (selectedClinicValue) {
                    item.properties.__clinic = selectedClinicValue;
                    this.debugLog(`[CLINIC-DEBUG] Updated item ${item.key} clinic to: ${selectedClinicValue}`);
                  } else if (typeof(item.properties.__clinic) !== 'undefined') {
                    delete item.properties.__clinic;
                    this.debugLog(`[CLINIC-DEBUG] Removed clinic property from item ${item.key}`);
                  }

                  clinicHasChanged = true;
                  updates.push({
                    id: item.key,
                    properties: item.properties,
                    quantity: item.quantity
                  });
                }
              }
            });

            if (clinicHasChanged && response.attributes && (
              response.attributes.delivery_till
              || (!response.attributes.delivery_till && this.querySelector('[name="delivery_till"]') && this.querySelector('[name="delivery_till"]').value)
            )) {
              clearDeliveryDate = true;
            }
          }

          this.debugLog(`[CLINIC-DEBUG] Prepared ${updates.length} updates for clinic properties`);

          this.changeCartItems(updates)
            .then(async res => {
              this.debugLog(`[CLINIC-DEBUG] Clinic properties update completed`);

              if (clearDeliveryDate) {
                this.debugLog(`[CLINIC-DEBUG] Clearing delivery date`);

                await fetch(window.Shopify.routes.root + "cart/update.js", {
                  ...fetchConfig("javascript"),
                  body: JSON.stringify({
                    attributes: {
                      delivery_till: ''
                    }
                  })
                }).then(() => {
                  if (this.deliveryDatePicker) {
                    this.deliveryDatePicker.clearSelection();
                  }

                  this.update();
                });
              }

              this.stopLoadingClinic('updateCurrentClinicProperties');

              if (typeof(callback) === 'function') {
                callback(res);
              }
            })
            .catch(error => {
              this.debugLog(`[CLINIC-DEBUG][ERROR] Failed to update clinic properties: ${error}`);
              this.stopLoadingClinic('updateCurrentClinicProperties');

              if (typeof(callback) === 'function') {
                callback();
              }
            });
      })
    }

    validate ()
    {
      let checkFirstStep = false;
      let firstStepValid = true;

      if (this.clinicInput && !this.querySelector('.don-shipment-settings').classList.contains('disabled')) {
        checkFirstStep = true;

        // The dropdown alone isn't enough: changeClinic can fail after a clinic
        // is picked (e.g. its Shopify product isn't on the Online Store), which
        // leaves no Clinic line in the cart. clinic_selected is only set when
        // that line exists (Liquid on render, changeClinic after a successful add).
        const clinicSelected = this.querySelector('input[name="clinic_selected"]');

        if (!this.clinicInput.value || !clinicSelected || !clinicSelected.value) {
          this.debugLog('[Validation] Current clinic not selected or not in cart');
          firstStepValid = false;
        }

        if (!this.querySelector('[name="delivery_till"]').classList.contains('hidden')
          && !this.querySelector('[name="delivery_till"]').value
        ) {
          this.debugLog('[Validation] Delivery date not selected');
          firstStepValid = false;
        }
      }

      if (this.bankInput && !this.querySelector('.don-storage-settings').classList.contains('disabled')) {
        checkFirstStep = true;

        if (!this.querySelector(`.bank-storage-time[data-bank-id="${this.bankInput.value}"] select`).value) {
          this.debugLog('[Validation] Storage time not selected');
          firstStepValid = false;
        }
      }

      const mot2Valid = this.validateMot2();
      if (!mot2Valid) {
        this.debugLog('[Validation] MOT 2 rule not satisfied');
        firstStepValid = false;
        checkFirstStep = true;
      }

      this.debugLog('[Validation] checkFirstStep:', checkFirstStep, 'firstStepValid:', firstStepValid);

      if (this.pregnancyQuotaMissing) {
        firstStepValid = false;
        checkFirstStep = true;
      }

      if (this.strawSetInvalid) {
        firstStepValid = false;
        checkFirstStep = true;
      }

      if (checkFirstStep) {
        if (firstStepValid) {
          console.debug('[Validation] Valid');
          if (this.cartNextStepButton) {
            this.cartNextStepButton.disabled = false;
            this.cartNextStepButton.classList.remove("disabled");
          }

          if (this.cartCheckoutButton) {
            this.cartCheckoutButton.disabled = false;
            this.cartCheckoutButton.classList.remove("disabled");
          }
        } else {
          console.debug('[Validation] Invalid');
          if (this.cartNextStepButton) {
            this.cartNextStepButton.disabled = true;
            this.cartNextStepButton.classList.add("disabled");
          }

          if (this.cartCheckoutButton) {
            this.cartCheckoutButton.disabled = true;
            this.cartCheckoutButton.classList.add("disabled");
          }
        }
      }

      if (this.querySelector(".cart-person")) {
        const form = this.querySelector(".cart-person form");
        const firstName = form.querySelector('[name="first_name"]');
        const lastName = form.querySelector('[name="last_name"]');
        const addPartner = form.querySelector('[name="add_partner"]');
        const partnerFirstName = form.querySelector('[name="partner_first_name"]');
        const partnerLastName = form.querySelector('[name="partner_last_name"]');
        const terms = form.querySelector('[name="terms"]');
        const privacy = form.querySelector('[name="privacy"]');
        const clinicTerms = form.querySelectorAll('.cart-person-clinic-terms input');

        let valid = true;

        if (!firstName.value || !lastName.value || (terms && !terms.checked) || !privacy.checked) {
          valid = false;
          console.debug('[Person Validation] Invalid name or not checked checkboxes');
        }

        clinicTerms.forEach(terms => {
          if (!terms.checked) {
            valid = false;
            console.debug('[Person Validation] Clinic terms not checked');
          }
        });

        if (addPartner.checked) {
          if (!partnerFirstName.value || !partnerLastName.value) {
            valid = false;
            console.debug('[Person Validation] Invalid partner name');
          }
        }

        if (this.validateAge() === false) {
          console.debug('[Person Validation] Invalid age');
          valid = false;
        }

        // Treatment date validation (required unless "I don't know" checked)
        const treatmentDateUnknown = form.querySelector('[name="treatment_date_unknown"]')?.checked;
        const treatmentDate = form.querySelector('[name="intended_treatment_date"]')?.value;

        if (!treatmentDateUnknown && !treatmentDate) {
          console.debug('[Person Validation] Treatment date not selected');
          valid = false;
        }

        if (this.pregnancyQuotaMissing) {
          valid = false;
        }

        if (this.strawSetInvalid) {
          valid = false;
        }

        if (valid) {
          console.debug('[Person Validation] Valid');
          if (this.cartNextStepButton) {
            this.cartNextStepButton.disabled = false;
            this.cartNextStepButton.classList.remove("disabled");
          }
        } else {
          console.debug('[Person Validation] Invalid');
          if (this.cartNextStepButton) {
            this.cartNextStepButton.disabled = true;
            this.cartNextStepButton.classList.add("disabled");
          }
        }
      }
    }

    update ()
    {
      this.updateBanks();
      this.validate();
      this.updateReservations();
      this.refreshPregnancyQuotaGate();
      this.refreshStrawSetGate();
    }

    // ONE rule for "this donor product already has a pregnancy quota in the
    // cart": a PregnancyQuota line anchored via __line_item_local_id to a
    // LIVING donor line of that product. Property stamps alone don't count
    // (they outlive the donor line they belonged to); unanchored quota lines
    // don't count either. Mirrors cart-table.liquid's pq_present and the
    // product-page pre-add check.
    cartHasPregnancyQuotaForProduct (cart, productId)
    {
      if (!cart || !cart.items) {
        return false;
      }

      return cart.items.some(quota => quota.product_type === 'PregnancyQuota'
        && quota.properties
        && quota.properties.__line_item_local_id
        && cart.items.some(donor => donor.product_type === 'Donor'
          && donor.product_id == productId
          && donor.properties
          && donor.properties.__item_local_id === quota.properties.__line_item_local_id));
    }

    validatePregnancyQuotas ()
    {
      // Only block when we have POSITIVE evidence the donor needs a PQ:
      //   1. The donor declared a PQ requirement (__pregnancy_quota property set), AND
      //   2. No PQ line item in cart is linked to ANY line of the same donor product, AND
      //   3. hasQuota resolved to FALSE for this donor + country (i.e., we have a
      //      confirmed-no answer from the customer-account API).
      // One paid PQ covers all cart lines of the same donor product (multi-variant or
      // multi-qty-1 of the same donor). Per-line linkage via __line_item_local_id is
      // preserved on the wire for order-sync (donornetwork SyncOrders) compatibility.
      //
      // Any other state (rejection, missing country, missing module) is treated as
      // "uncertain — don't block" so legitimate account-quota holders aren't punished
      // by transient API errors or malformed quota records. This mirrors the tolerance
      // of updateReservations(), which has no .catch and leaves the upsell row in its
      // initial hidden state if hasQuota rejects.
      // Resolves { missing: boolean, productIds: [String] } — the ids let the
      // gate reveal only the upsell rows of donors actually missing a quota.
      const noneMissing = { missing: false, productIds: [] };

      return this.fetchCustomCart().then(response => {
        if (!response || !response.items || response.items.length === 0) {
          return noneMissing;
        }

        const donors = response.items.filter(i => i.product_type === 'Donor');
        if (donors.length === 0) {
          return noneMissing;
        }

        const treatmentCountry = this.getTreatmentSelection().country;

        const checks = donors.map(donor => {
          const pqRef = donor.properties && donor.properties.__pregnancy_quota;
          if (!pqRef) {
            return Promise.resolve(null);
          }

          if (this.cartHasPregnancyQuotaForProduct(response, donor.product_id)) {
            return Promise.resolve(null);
          }

          if (!treatmentCountry || !app.pregnancyQuotas) {
            // Can't make a confident call — don't block legitimate checkouts.
            return Promise.resolve(null);
          }

          return app.pregnancyQuotas.hasQuota(donor.product_title, treatmentCountry)
            .then(owned => owned ? null : String(donor.product_id))
            .catch(err => {
              console.warn('[PQ Gate] hasQuota rejected for donor', donor.product_title, '— treating as uncertain (not blocking):', err);
              return null;
            });
        });

        return Promise.all(checks).then(results => {
          const productIds = results.filter(Boolean);

          return { missing: productIds.length > 0, productIds: productIds };
        });
      }).catch(err => {
        console.warn('[PQ Gate] validatePregnancyQuotas failed — not blocking:', err);
        return noneMissing;
      });
    }

    refreshPregnancyQuotaGate ()
    {
      return this.validatePregnancyQuotas().then(result => {
        const previous = this.pregnancyQuotaMissing;
        this.pregnancyQuotaMissing = result.missing;

        if (result.missing) {
          // Only reveal the upsell rows of donors actually missing a quota —
          // a blanket reveal resurrected rows updateReservations had hidden
          // (account-quota holders, already-covered products), inviting a
          // second quota purchase.
          this.querySelectorAll('.pregnancy-quota-add').forEach(row => {
            const addButton = row.querySelector('.cart-add-quota');

            if (addButton && result.productIds.indexOf(addButton.dataset.productId) !== -1) {
              row.classList.remove('hidden');
            }
          });
        }

        if (previous !== result.missing) {
          this.validate();
        }

        return result.missing;
      });
    }

    runPregnancyQuotaGate ()
    {
      return this.refreshPregnancyQuotaGate().then(missing => {
        if (missing) {
          const firstRow = this.querySelector('.pregnancy-quota-add:not(.hidden)');
          if (firstRow) {
            firstRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
        return missing;
      });
    }

    // ---- Straw-set gate (e.g. AU sets of 6) ------------------------------
    // Mirrors the pregnancy-quota gate wiring: validate → refresh banner and
    // button state → run as a hard stop from the checkout/next-step clicks.
    // The rule itself lives in app.strawSets (dev/js/core/app/_straw_sets.js);
    // this is a THEME-ONLY rule, so this gate and the per-line steppers are
    // the enforcement — there is no backend or checkout-side check behind it.

    validateStrawSets ()
    {
      const none = { invalid: false, violations: [] };

      if (!window.app || !window.app.strawSets || !window.app.strawSets.hasRules()) {
        return Promise.resolve(none);
      }

      return this.fetchCustomCart().then(response => {
        if (!response || !response.items || response.items.length === 0) {
          return none;
        }

        // cart?view=custom carries per-line inventory, so this also catches
        // set lines whose stock has dropped below a whole set (see
        // exceedsAlignedStock) — Shopify would otherwise reduce them to a
        // partial set at checkout.
        const violations = window.app.strawSets.findViolations(response.items);

        return { invalid: violations.length > 0, violations: violations };
      }).catch(err => {
        // Same tolerance as the PQ gate: an unreadable cart must not block
        // legitimate checkouts.
        console.warn('[StrawSet Gate] validateStrawSets failed — not blocking:', err);

        return none;
      });
    }

    refreshStrawSetGate ()
    {
      return this.validateStrawSets().then(result => {
        const previous = this.strawSetInvalid;
        this.strawSetInvalid = result.invalid;

        const banner = this.querySelector('[data-straw-set-error]');

        if (banner) {
          if (result.invalid) {
            // Write into the text span, not the banner itself — the banner
            // also holds the step-2 "back to cart" link.
            const text = banner.querySelector('[data-straw-set-error-text]') || banner;

            text.textContent = (banner.dataset.strawSetErrorTemplate || '')
              .replace('[quantity]', result.violations[0].size);
            banner.classList.remove('hidden');
          } else {
            banner.classList.add('hidden');
          }
        }

        // Highlight the offending steppers — the inputs' 1-based data-index
        // matches the cart line position the violations carry.
        this.querySelectorAll('.cart-quantity.straw-set-violation')
          .forEach(div => div.classList.remove('straw-set-violation'));

        result.violations.forEach(violation => {
          const input = this.querySelector(`.quantity__input[data-index="${violation.line}"]`);
          const wrapper = input ? input.closest('.cart-quantity') : null;

          if (wrapper) {
            wrapper.classList.add('straw-set-violation');
          }
        });

        if (previous !== result.invalid) {
          this.validate();
        }

        return result.invalid;
      });
    }

    scrollToStrawSetError ()
    {
      const banner = this.querySelector('[data-straw-set-error]:not(.hidden)');

      if (banner) {
        banner.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    updateReservations ()
    {
      // One live-cart read shared by all rows (in-flight requests are deduped
      // with the gate's read in the same update() cycle). The rendered rows
      // can be stale — e.g. a quota that landed after this page's HTML was
      // rendered, or one added from another tab — so "already covered in
      // cart" hides the row just like an account-level quota does.
      const customCartPromise = this.addPregancyQuota.length > 0
        ? this.fetchCustomCart()
        : Promise.resolve(null);

      this.addPregancyQuota.forEach((button) => {
        Promise.all([
          app.pregnancyQuotas.hasQuota(button.dataset.productTitle, this.getTreatmentSelection().country),
          customCartPromise
        ])
          .then(([hasQuota, customCart]) => {
            const pregnancyQuotaVariant = app.pregnancyQuotas.getPregnancyQuotaVariant(undefined, undefined, button.dataset.familyLimit);
            const row = button.closest('.pregnancy-quota-add');

            if (hasQuota || this.cartHasPregnancyQuotaForProduct(customCart, button.dataset.productId)) {
              row.classList.add('hidden');

              return;
            }

            row.querySelectorAll('[data-pregnancy-quota-variant-id]').forEach(quota => {
              if (quota.dataset.pregnancyQuotaVariantId != pregnancyQuotaVariant.variantId) {
                quota.style.display = 'none';
              } else {
                quota.style.display = 'block';
              }
            });

            row.classList.remove('hidden');

            if (!button.classList.contains('js-initialized')) {
              button.classList.add('js-initialized');
              button.addEventListener('click', e => {
                e.preventDefault();

                this.addPregnancyQuotaToReservation(button);
              });
            }
          });
      });

      this.removePregancyQuota.forEach(button => {
        if (button.classList.contains('js-initialized')) {
          return;
        }

        button.classList.add('js-initialized');
        button.addEventListener('click', e => {
          e.preventDefault();

          this.startLoadingDonor('removePregnancyQuota');
          this.cartChange({
            id: button.dataset.lineItemKey,
            quantity: 0
          }).then(response => {
            if (!response.items || response.items.length === 0) {
              this.stopLoadingDonor('removePregnancyQuota');

              return;
            }

            response.items.forEach(item => {
              if (!item.properties
                || item.properties.__item_local_id !== button.dataset.lineItemLocalId
                || !item.properties.__pregnancy_quota
              ) {
                return;
              }

              delete item.properties.__pregnancy_quota;

              this.startLoadingDonor('removePregnancyQuotaProperties');
              this.cartChange({
                id: item.key,
                properties: item.properties,
                quantity: item.quantity
              }).then(res => {
                this.fetchCartSections().then(() => {
                  this.stopLoadingDonor('removePregnancyQuotaProperties');
                });
              });
            });

            this.stopLoadingDonor('removePregnancyQuota');
          });
        });
      });
    }

    addPregnancyQuotaToReservation (button)
    {
      const pregnancyQuotaVariant = app.pregnancyQuotas.getPregnancyQuotaVariant(undefined, undefined, button.dataset.familyLimit);
      const cart = document.querySelector("cart-items");

      const pregnancyQuotaString = pregnancyQuotaVariant.productId + ':' + pregnancyQuotaVariant.variantId;
      const quotaItem = {
        id: pregnancyQuotaVariant.variantId,
        quantity: 1,
        properties: {
          __variant_id: button.dataset.variantId,
          __line_item_id: button.dataset.lineItemId,
          __line_item_local_id: button.dataset.itemLocalId,
          'For': button.dataset.productTitle
        }
      };
      // An exclusive-tier quota brings its nonrefundable fee in the same add.
      const feeItem = app.pregnancyQuotas.exclusivityFeeLine(quotaItem.id, quotaItem.properties);
      const data = {
        items: feeItem ? [quotaItem, feeItem] : [quotaItem],
        sections: cart.getSectionsToRender().map(section => section.id),
        sections_url: window.location.pathname
      };

      this.startLoadingDonor('addPregnancyQuota');

      // The row this button lives in can be stale — rendered before a quota
      // for this donor landed (another tab, or a product-page add still in
      // flight when this page was served). Check the live cart first: a
      // covered product just refreshes the sections so the stale row goes
      // away, instead of adding a second quota.
      this.fetchCustomCart().then(customCart => {
        if (this.cartHasPregnancyQuotaForProduct(customCart, button.dataset.productId)) {
          this.fetchCartSections().finally(() => {
            this.stopLoadingDonor('addPregnancyQuota');
          });

          return;
        }

        this.addPregnancyQuotaLine(data, button, pregnancyQuotaString);
      }).catch(error => {
        console.error('Pregnancy quota pre-check failed:', error);
        this.stopLoadingDonor('addPregnancyQuota');
      });
    }

    addPregnancyQuotaLine (data, button, pregnancyQuotaString)
    {
      this.cartAdd(data).then(response => {
        this.fetchCart().then(response => {
          if (!response.items || response.items.length === 0) {
            this.stopLoadingDonor('addPregnancyQuota');

            return;
          }

          response.items.forEach(item => {
            if (item.id != button.dataset.lineItemId) {
              return;
            }

            if (item.properties && item.properties.__pregnancy_quota === pregnancyQuotaString) {
              return;
            }

            item.properties.__pregnancy_quota = pregnancyQuotaString;

            this.startLoadingDonor('addPregnancyQuotaProperties');
            this.cartChange({
              id: item.key,
              properties: item.properties,
              quantity: item.quantity
            }).then(res => {
              this.fetchCartSections().then(() => {
                this.stopLoadingDonor('addPregnancyQuotaProperties');
              });
            });
          });

          this.stopLoadingDonor('addPregnancyQuota');
        });
      });
    }

    validateMot2 () {
      let valid = true;
      const donorRows = this.querySelectorAll('.don-table-donor');
      const donorsByProduct = {};

      donorRows.forEach(row => {
        const productId = row.dataset.donorProductId;
        const title = row.querySelector('.don-table-content-header--type')?.textContent?.trim();
        if (!donorsByProduct[productId]) donorsByProduct[productId] = [];
        donorsByProduct[productId].push({ row, title });
      });

      this.mot2CompanionRows.forEach(pickerRow => {
        const productId = pickerRow.dataset.mot2ProductId;
        const donors = donorsByProduct[productId] || [];
        const hasCompanion = donors.some(d => !this.isMot2Variant(d.title));
        const acknowledged = pickerRow.querySelector('.mot2-acknowledge-checkbox')?.checked;

        this.debugLog('[MOT2] productId:', productId, 'hasCompanion:', hasCompanion, 'acknowledged:', acknowledged);

        // Update warning badges on donor rows
        donors.forEach(d => {
          const badge = d.row.querySelector('.mot2-warning-badge');
          if (badge) {
            if (hasCompanion || acknowledged) {
              badge.classList.add('hidden');
            } else if (this.isMot2Variant(d.title)) {
              badge.classList.remove('hidden');
            }
          }
        });

        if (hasCompanion) {
          // Companion straw in cart — hide the section entirely
          pickerRow.classList.add('hidden');
        } else {
          // No companion — show the section (picker + acknowledgment)
          pickerRow.classList.remove('hidden');
          this.initMot2CompanionPicker(pickerRow);

          // Disable/enable picker based on checkbox state
          const picker = pickerRow.querySelector('.mot2-companion-picker');
          if (picker) {
            if (acknowledged) {
              picker.classList.add('mot2-picker-disabled');
            } else {
              picker.classList.remove('mot2-picker-disabled');
            }
          }

          if (!acknowledged) {
            valid = false;
          }
        }
      });

      return valid;
    }

    async initMot2CompanionPicker (row) {
      if (row.classList.contains('js-mot2-initialized')) return;
      row.classList.add('js-mot2-initialized');

      const handle = row.dataset.mot2ProductHandle;
      const currentVariantId = row.dataset.mot2VariantId;
      const loader = row.querySelector('.mot2-companion-picker-loader');
      const optionsContainer = row.querySelector('.mot2-companion-picker-options');
      const emptyMessage = row.querySelector('.mot2-companion-picker-empty');

      try {
        const response = await fetch(`/products/${handle}.js`);
        const product = await response.json();

        this.debugLog('[MOT2] Product data for', handle, ':', product);
        this.debugLog('[MOT2] All variants:', product.variants);

        const eligibleVariants = product.variants.filter(v => {
          const title = (v.title || '').toLowerCase();
          const option1 = (v.option1 || '').toLowerCase();
          const isAvailable = v.available;
          const isCurrent = v.id == currentVariantId;
          const isMot2 = this.isMot2Variant(v.title);
          const isCryo = option1.includes('cryo') || title.includes('cryo');
          const isReservation = option1.includes('reservation') || title.includes('reservation');

          this.debugLog('[MOT2] Variant:', v.title, '| available:', isAvailable, '| isMot2:', isMot2, '| isCryo:', isCryo, '| isReservation:', isReservation);

          return isAvailable && !isCurrent && !isMot2 && !isCryo && !isReservation;
        });

        this.debugLog('[MOT2] Eligible variants:', eligibleVariants);

        loader.classList.add('hidden');

        if (eligibleVariants.length === 0) {
          emptyMessage.classList.remove('hidden');
          return;
        }

        const addLabel = row.querySelector('.mot2-companion-picker').dataset.addLabel || 'Add';
        optionsContainer.innerHTML = eligibleVariants.map(v => `
          <div class="mot2-companion-option">
            <span class="mot2-companion-option-name">${v.title}</span>
            <span class="mot2-companion-option-price">${app.money(v.price / 100).replace(window.theme.shop.money_sign, '').trim()}</span>
            <a href="#" class="button button--secondary btn-add-to-cart mot2-companion-add-btn" data-variant-id="${v.id}">
              ${addLabel}
            </a>
          </div>
        `).join('');

        optionsContainer.classList.remove('hidden');

        optionsContainer.querySelectorAll('.mot2-companion-add-btn').forEach(btn => {
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            this.addMot2Companion(row, btn.dataset.variantId);
          });
        });
      } catch (error) {
        this.debugLog('[MOT2] Error fetching product variants:', error);
        loader.classList.add('hidden');
        emptyMessage.classList.remove('hidden');
      }
    }

    addMot2Companion (row, variantId) {
      if (!variantId) return;
      const shipping = row.dataset.mot2Shipping;
      const treatment = row.dataset.mot2Treatment;

      const properties = {
        __item_local_id: Date.now().toString(36) + Math.random().toString(36).substring(2),
        __shipping: shipping
      };

      if (treatment) {
        properties.__treatment = treatment;
      }

      const data = {
        items: [{
          id: parseInt(variantId),
          quantity: 1,
          properties: properties
        }]
      };

      this.startLoadingDonor('addMot2Companion');

      this.cartAdd(data).then(() => {
        window.location.reload();
      });
    }

    validateAge() {
      const dayDropdown = document.getElementById('person-birthday-day');
      const monthDropdown = document.querySelector('[name="birthday_month"]');
      const yearDropdown = document.querySelector('[name="birthday_year"]');

      const selectedDay = parseInt(dayDropdown.value);
      const selectedMonth = parseInt(monthDropdown.value);
      const selectedYear = parseInt(yearDropdown.value);

      const age = this.calculateAge(selectedDay, selectedMonth, selectedYear);

      return age >= 18;
    }

    calculateAge(birthDay, birthMonth, birthYear) {
      const today = new Date();
      const selectedDate = new Date(birthYear, birthMonth - 1, birthDay);
      let age = today.getFullYear() - selectedDate.getFullYear();
      const monthDifference = today.getMonth() - selectedDate.getMonth();
      if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < selectedDate.getDate())) {
        age--;
      }
      return age;
    }

    updateBanks ()
    {
      if (!this.storageSettings) {
        return;
      }

      this.storageSettings.querySelectorAll(".bank-storage-time").forEach(time => time.classList.add("hidden"));
      this.storageSettings.querySelector(".don-storage-settings-field").classList.remove("hidden");

      if (this.bankInput.value) {
        this.storageSettings.querySelectorAll('.bank-storage-time').forEach(time => {
          if (time.dataset.bankId == this.bankInput.value) {
            time.classList.remove("hidden")
          }
        });
      } else {
        this.storageSettings.querySelector(".don-storage-settings-field").classList.add("hidden");
      }
    }

    updatePerson (callback)
    {
      const form = this.querySelector(".cart-person form");
      const attributes = {
        first_name: form.querySelector('[name="first_name"]').value,
        last_name: form.querySelector('[name="last_name"]').value,
        birthday: "",
        include_partner: form.querySelector('[name="add_partner"]').checked ? 'yes' : 'no',
        accepted_privacy: form.querySelector('[name="privacy"]').checked ? 'yes' : 'no',
        partner_first_name: null,
        partner_last_name: null,
        intended_treatment_date: form.querySelector('[name="treatment_date_unknown"]')?.checked
          ? 'Unknown'
          : (form.querySelector('[name="intended_treatment_date"]')?.value || '')
      };

      if (form.querySelector('[name="terms"]')) {
        attributes.accepted_terms = form.querySelector('[name="terms"]').checked ? 'yes' : 'no';
      } else {
        let acceptedTerms = false;

        form.querySelectorAll('[type="checkbox"]').forEach(input => {
          if (input.name.startsWith('clinic_terms') && input.checked) {
            acceptedTerms = true;
          }
        });

        attributes.accepted_terms = acceptedTerms ? 'yes' : 'no';
      }

      if (form.querySelector('[name="birthday_day"]').value < 10) {
        attributes.birthday += "0" + form.querySelector('[name="birthday_day"]').value + '.';
      } else {
        attributes.birthday += form.querySelector('[name="birthday_day"]').value + '.';
      }

      if (form.querySelector('[name="birthday_month"]').value < 10) {
        attributes.birthday += "0" + form.querySelector('[name="birthday_month"]').value + '.';
      } else {
        attributes.birthday += form.querySelector('[name="birthday_month"]').value + '.';
      }

      attributes.birthday += form.querySelector('[name="birthday_year"]').value;

      if (form.querySelector('[name="add_partner"]').checked) {
        attributes.partner_first_name = form.querySelector('[name="partner_first_name"]').value;
        attributes.partner_last_name = form.querySelector('[name="partner_last_name"]').value;
      }

      fetch(window.Shopify.routes.root + "cart/update.js", {
        ...fetchConfig("javascript"),
        body: JSON.stringify({
          attributes: attributes
        })
      }).then(res => res.json()).then(res => {
        if (typeof(callback) === 'function') {
          callback(res);
        }
      });
    }

    initGeneticMatching ()
    {
      this.addGeneticMatching.forEach(button => {
        button.addEventListener('click', e => {
          e.preventDefault();

          const cartNotification = document.querySelector("cart-notification") || document.querySelector("cart-drawer");
          const cart = document.querySelector("cart-items");
          const geneticMatchingId = button.dataset.geneticMatchingProductId + ':' + button.dataset.geneticMatchingVariantId;
          const data = {
            items: [
              {
                id: button.dataset.geneticMatchingVariantId,
                quantity: 1,
                properties: {
                  __variant_id: button.dataset.variantId,
                  __line_item_id: button.dataset.lineItemId,
                  __line_item_local_id: button.dataset.itemLocalId,
                  __line_item_key: button.dataset.lineItemKey,
                  'For': button.dataset.productTitle
                }
              }
            ],
            sections: cart.getSectionsToRender().map(section => section.id),
            sections_url: window.location.pathname
          };

          data.items.push();

          this.startLoadingDonor('addGeneticMatching');

          this.cartAdd(data).then(response => {
            this.fetchCart().then(response => {
              if (!response.items || response.items.length === 0) {
                this.stopLoadingDonor('addGeneticMatching');

                return;
              }

              response.items.forEach(item => {
                if (item.id != button.dataset.lineItemId) {
                  return;
                }

                if (item.properties && item.properties.__genetic_matching === geneticMatchingId) {
                  return;
                }

                item.properties.__genetic_matching = geneticMatchingId;

                this.startLoadingDonor('addGeneticMatchingProperties');
                this.cartChange({
                  id: item.key,
                  properties: item.properties,
                  quantity: item.quantity
                }).then(res => {
                  this.fetchCartSections().then(() => {
                    this.stopLoadingDonor('addGeneticMatchingProperties');
                  });
                });
              });

              this.stopLoadingDonor('addGeneticMatching');
            });
          });
        });
      });

      this.removeGeneticMatching.forEach(button => {
        button.addEventListener('click', e => {
          e.preventDefault();

          const geneticMatchingId = button.dataset.productId + ':' + button.dataset.variantId;

          this.startLoadingDonor('removeGeneticMatching');
          this.cartChange({
            id: button.dataset.lineItemKey,
            quantity: 0
          }).then(response => {
            if (!response.items || response.items.length === 0) {
              this.stopLoadingDonor('removeGeneticMatching');
              return;
            }

            response.items.forEach(item => {
              if (!item.properties
                || item.properties.__genetic_matching !== geneticMatchingId
                || item.properties.__item_local_id !== button.dataset.lineItemLocalId
                || button.dataset.donorVariantId != item.variant_id
              ) {
                return;
              }

              delete item.properties.__genetic_matching;

              this.startLoadingDonor('removeGeneticMatchingProperties');
              this.cartChange({
                id: item.key,
                properties: item.properties,
                quantity: item.quantity
              }).then(res => {
                this.fetchCartSections().then(() => {
                  this.stopLoadingDonor('removeGeneticMatchingProperties');
                });
              });
            });

            this.stopLoadingDonor('removeGeneticMatching');
          });
        });
      });
    }

    initTreatments ()
    {
      this.removeTreatment.forEach(remove => {
        remove.addEventListener('click', e => {
          e.preventDefault();

          this.startLoadingTreatment('removeTreatment');
          this.cartChange({
            id: remove.dataset.lineItemKey,
            quantity: 0
          }).then(response => {
            this.fetchCartSections().then(() => {
              this.stopLoadingTreatment('removeTreatment');
            });
          });
        });
      });
    }

    removeInvalidProducts ()
    {
      this.fetchCustomCart().then(response => {
        if (!response.items || response.items.length === 0) {
          return response;
        }

        this.debugger.log('[Cart] About to remove invalid products', response.items);

        const items = [];
        const donors = [];
        const updates = {};
        // Computed up front — the old code only discovered donors while
        // scanning PregnancyQuota lines, so a cart with donors but no quota
        // line could take the "no donors → clear everything" branch below.
        const hasDonor = response.items.some(item => item.product_type === 'Donor');

        // --- Pregnancy quotas: keep exactly ONE anchored quota per donor product.
        // Anchored = __line_item_local_id points at a living donor line — the
        // rule the checkout gate, the render and the add paths all use.
        // Everything else must not survive: orphans whose donor line is gone
        // (invisible in the cart table but still charged), duplicates from
        // historical add races, and quantity>1 merge artefacts. The old check
        // (any donor property === "product:variant") kept every orphan alive —
        // all donors share one quota product, so ANY stamped donor line
        // matched EVERY quota line — and deleted legitimately anchored quotas
        // on unstamped lines (e.g. a MOT 2 companion that inherited one).
        const donorByLocalId = {};
        response.items.forEach(item => {
          if (item.product_type === 'Donor' && item.properties && item.properties.__item_local_id) {
            donorByLocalId[item.properties.__item_local_id] = item;
          }
        });

        // Resolved per anchor donor so a max-5 capped donor prefers its
        // flat-priced variant among duplicates, not the cart-wide bucket.
        const desiredQuotaVariantIdFor = anchor => {
          try {
            const desiredQuotaVariant = app.pregnancyQuotas.getPregnancyQuotaVariant(
              undefined,
              undefined,
              anchor.metafields && anchor.metafields['donor.global_family_limit']
            );

            return desiredQuotaVariant ? desiredQuotaVariant.variantId : null;
          } catch (error) {
            // No quota config on this layout — duplicate collapse keeps the first line.
            return null;
          }
        };

        const quotaToKeepByProduct = {};
        response.items.forEach(quota => {
          if (quota.product_type !== 'PregnancyQuota') {
            return;
          }

          const anchor = quota.properties && quota.properties.__line_item_local_id
            ? donorByLocalId[quota.properties.__line_item_local_id]
            : null;

          if (!anchor) {
            return;
          }

          const current = quotaToKeepByProduct[anchor.product_id];
          const desiredQuotaVariantId = desiredQuotaVariantIdFor(anchor);

          if (!current) {
            quotaToKeepByProduct[anchor.product_id] = quota;
          } else if (desiredQuotaVariantId
            && current.variant_id != desiredQuotaVariantId
            && quota.variant_id == desiredQuotaVariantId
          ) {
            // Among duplicates, prefer the line already priced in the
            // currently-resolved variant bucket.
            quotaToKeepByProduct[anchor.product_id] = quota;
          }
        });

        const keptQuotaKeys = {};
        Object.keys(quotaToKeepByProduct).forEach(productId => {
          keptQuotaKeys[quotaToKeepByProduct[productId].key] = true;
        });

        // --- Exclusivity fees follow their quota: exactly one per kept
        // exclusive-tier quota, in that quota's tier, on the quota's anchor
        // line. Every other fee line goes (donor removed, quota dropped as a
        // duplicate, tier changed, merge artefact). A kept exclusive quota
        // with no fee gets one — that's also how carts holding a quota from
        // before fees went live pick theirs up.
        const keptFeeKeys = {};
        const feeLinesToAdd = [];
        Object.keys(quotaToKeepByProduct).forEach(productId => {
          const quota = quotaToKeepByProduct[productId];
          const feeVariant = app.pregnancyQuotas.exclusivityFeeVariantFor(quota.variant_id);

          if (!feeVariant) {
            return;
          }

          const fee = response.items.find(fee => fee.product_type === 'ExclusivityFee'
            && fee.variant_id == feeVariant.variantId
            && fee.properties
            && fee.properties.__line_item_local_id === quota.properties.__line_item_local_id);

          if (fee) {
            keptFeeKeys[fee.key] = true;
          } else {
            feeLinesToAdd.push(app.pregnancyQuotas.exclusivityFeeLine(quota.variant_id, quota.properties));
          }
        });

        // Adding a missing fee reloads the page; capped per session so a fee
        // that can't be added (product unpublished, variant deleted) can't
        // reload-loop the cart.
        const addMissingExclusivityFees = () => {
          const attempts = parseInt(app.session.get('exclusivity_fee_repairs'), 10) || 0;

          if (feeLinesToAdd.length === 0) {
            app.session.put('exclusivity_fee_repairs', 0);

            return Promise.resolve(false);
          }

          if (attempts >= 2) {
            console.error('[Cart] Gave up adding missing exclusivity fees', feeLinesToAdd);

            return Promise.resolve(false);
          }

          app.session.put('exclusivity_fee_repairs', attempts + 1);
          this.debugger.log('[Cart] Adding missing exclusivity fees', feeLinesToAdd);

          return this.cartAdd({ items: feeLinesToAdd })
            .then(response => !response.status)
            .catch(error => {
              console.error('[Cart] Adding missing exclusivity fees failed', error);

              return false;
            });
        };

        response.items.forEach(item => {
          if (item.product_type === 'PregnancyQuota') {
            if (!keptQuotaKeys[item.key]) {
              this.debugger.log('[Cart] Removing unanchored or duplicate Pregnancy Quota');

              items.push(item.key);
              updates[item.key] = 0;
            } else if (item.quantity > 1) {
              // Merge artefact (two identical adds collapsed by Shopify into
              // one line). One quota covers all lines of the donor product —
              // quantity above 1 is never valid.
              this.debugger.log('[Cart] Normalizing Pregnancy Quota quantity to 1');

              items.push(item.key);
              updates[item.key] = 1;
            }
          } else if (item.product_type === 'ExclusivityFee') {
            if (!keptFeeKeys[item.key]) {
              this.debugger.log('[Cart] Removing Exclusivity Fee without its quota');

              items.push(item.key);
              updates[item.key] = 0;
            } else if (item.quantity > 1) {
              this.debugger.log('[Cart] Normalizing Exclusivity Fee quantity to 1');

              items.push(item.key);
              updates[item.key] = 1;
            }
          } else if (item.product_type === 'Clinic') {
            const isTreatmentVariant = item.properties && item.properties.__treatment_target_local_id;
            const isShipmentVariant = !isTreatmentVariant;

            if (isShipmentVariant) {
              let clinicInUse = false;

              response.items.forEach(donor => {
                if (donor.product_type === 'Donor' && donor.properties && donor.properties.__clinic === item.properties.__destination + ':' + item.variant_id) {
                  clinicInUse = true;
                }
              });

              if (!clinicInUse) {
                this.debugger.log('[Cart] Removing unused Clinic');

                items.push(item.key);
                updates[item.key] = 0;
              }
            } else if (isTreatmentVariant) {
              let treatmentInUse = false;

              response.items.forEach(donor => {
                if (donor.product_type === 'Donor'
                  && donor.properties
                  && donor.properties.__item_local_id === item.properties.__treatment_target_local_id
                ) {
                  treatmentInUse = true;
                }
              });

              if (!treatmentInUse) {
                this.debugger.log('[Cart] Removing unused Treatment');

                items.push(item.key);
                updates[item.key] = 0;
              }
            }
          } else if (item.product_type === 'Bank') {
            let bankInUse = false;

            response.items.forEach(donor => {
              if (donor.product_type === 'Donor' && donor.properties && donor.properties.__cryogenic_storage === item.product_id + ':' + item.variant_id) {
                bankInUse = true;
              }
            });

            if (!bankInUse) {
              this.debugger.log('[Cart] Removing unused Bank');

              items.push(item.key);
              updates[item.key] = 0;
            }
          } else if (item.product_type === 'ExpressShipping') {
            const deliveryTill = response.attributes && response.attributes.delivery_till;
            let isValidExpress = false;

            if (deliveryTill) {
              const date = moment(deliveryTill, 'DD-MM-YYYY');
              const expressStart = moment().add(checkWeekendInRange(new Date(), 3), 'days');
              const expressEnd = moment().add(7, 'days');
              isValidExpress = date.isBetween(expressStart, expressEnd, 'day', '[]');
            }

            // Express is never valid when the cart holds a donor from an
            // express-blocked vendor (covers express added before the donor).
            if (isValidExpress && response.items.some(donor =>
              donor.product_type === 'Donor' && isExpressBlockedVendor(donor.vendor)
            )) {
              isValidExpress = false;
            }

            if (!isValidExpress) {
              this.debugger.log('[Cart] Removing orphaned Express Shipping');

              items.push(item.key);
              updates[item.key] = 0;

              const banner = document.querySelector('#express-shipping-banner');
              if (banner) banner.classList.add('hidden');
            }
          } else if (item.product_type === 'Donor'
              && (!item.properties || (!item.properties.__shipping && !item.properties.__reservation_variant_id))
          ) {
            donors.push(item);
          }
          if (item.product_type === 'Donor') {
            this.donors.push(item);
          }
        });

        if (items.length > 0 && !hasDonor) {
          this.debugger.log('[Cart] Remove invalid old products', items);

          fetch(window.Shopify.routes.root + "cart/clear.js", fetchConfig("javascript"))
            .then(response => response.json())
            .then(response => {
              window.location.reload();
            });
        } else if (items.length > 0) {
          this.debugger.log('[Cart] Remove invalid products, bo no donors', updates);

          fetch(window.Shopify.routes.root + "cart/update.js", {
            ...fetchConfig("javascript"),
            body: JSON.stringify({
              updates: updates
            })
          }).then(response => response.json())
            .then(() => addMissingExclusivityFees())
            .then(response => {
              window.location.reload();
            });
        } else if (donors.length > 0) {
          this.debugger.log('[Cart] Remove invalid products, donors', donors);

          donors.forEach(donor => {
            let retries = app.session.get('remove_donor_' + donor.key + '_count');

            if (!retries) {
              retries = 0;
            }

            if (retries > 0) {
              app.session.put('remove_donor_' + donor.key + '_count', 0);

              fetch(window.Shopify.routes.root + "cart/clear.js", fetchConfig("javascript"))
                .then(response => response.json())
                .then(response => {
                  window.location.reload();
                });

              return;
            }

            retries++;

            app.session.put('remove_donor_' + donor.key + '_count', retries);

            this.debugger.log('[Cart] Remove invalid products, donor', donor);

            this.removeDonor(donor.product_id, donor.variant_id, donor.key, donor.properties.__item_local_id, () => {
              window.location.reload();
            });
          });
        } else {
          addMissingExclusivityFees().then(added => {
            if (added) {
              window.location.reload();
            }
          });
        }

        return response;
      });
    }

    updateTreatmentCountry ()
    {
      return new Promise((resolve, reject) => {
        this.startLoadingDonor('*');

        this.fetchCustomCart().then(response => {
          if (!response.items || response.items.length === 0) {
            this.stopLoadingDonor('*');
            resolve();
            return response;
          }

          const updates = [];
          const selection = this.getTreatmentSelection();

          response.items.forEach(donor => {
            if (donor.product_type === 'Donor') {
              let changed = false;

              if (donor.properties.__treatment !== selection.country) {
                donor.properties.__treatment = selection.country;
                changed = true;
              }

              // The AU state only rides along on the line when Australia is selected.
              const desiredState = selection.country === 'AU'
                ? (selection.state || '')
                : '';

              if (desiredState) {
                if (donor.properties.__treatment_state !== desiredState) {
                  donor.properties.__treatment_state = desiredState;
                  changed = true;
                }
              } else if (donor.properties.__treatment_state) {
                delete donor.properties.__treatment_state;
                changed = true;
              }

              if (changed) {
                updates.push({
                  id: donor.key,
                  properties: donor.properties,
                  quantity: donor.quantity
                });
              }
            }
          });

          this.changeCartItems(updates)
            .then(() => {
              this.stopLoadingDonor('*');
              resolve();
            })
            .catch(error => {
              this.stopLoadingDonor('*');
              reject(error);
            });

          return response;
        }).catch(error => {
          this.stopLoadingDonor('*');
          reject(error);
        });
      });
    }

    checkQuotaVariant ()
    {
      // Reentrancy guard. Two overlapping runs share one custom-cart snapshot
      // (fetchCustomCart dedupes in-flight requests), compute the same plan,
      // and both execute it — the second remove fails silently (Shopify error
      // responses still resolve) and the two identical adds merge into a
      // single quantity-2 quota line. One run at a time; a second call while
      // busy has nothing to add anyway, and removeInvalidProducts converges
      // any state a skipped run would have fixed.
      if (this.checkQuotaVariantBusy) {
        return;
      }

      this.checkQuotaVariantBusy = true;

      this.fetchCustomCart().then(response => {
        if (!response.items || response.items.length === 0) {
          this.checkQuotaVariantBusy = false;

          return response;
        }

        const remove = {};
        const add = [];
        const change = [];
        // One replacement quota per donor product, even if a corrupted cart
        // still holds several quota lines for it — every stale line is
        // removed, exactly one is re-added.
        const plannedProducts = {};

        response.items.forEach(quota => {
          if (quota.product_type === 'PregnancyQuota') {
            response.items.forEach(donor => {
              if (donor.product_type === 'Donor'
                && donor.properties.__item_local_id === quota.properties.__line_item_local_id
              ) {
                // Resolved per donor — a max-5 capped donor keeps its flat-priced
                // variant while the rest of the cart follows the country bucket.
                const pregnancyQuotaVariant = app.pregnancyQuotas.getPregnancyQuotaVariant(
                  undefined,
                  undefined,
                  donor.metafields && donor.metafields['donor.global_family_limit']
                );

                // Pregnancy quota requires variant change
                if (pregnancyQuotaVariant.variantId != quota.variant_id) {
                  console.log('PQ requires change');
                  remove[quota.key] = 0;

                  // The old quota's exclusivity fee goes with it; the
                  // replacement brings its own tier's fee below.
                  response.items.forEach(fee => {
                    if (fee.product_type === 'ExclusivityFee'
                      && fee.properties
                      && fee.properties.__line_item_local_id === quota.properties.__line_item_local_id
                    ) {
                      remove[fee.key] = 0;
                    }
                  });

                  if (plannedProducts[donor.product_id]) {
                    return;
                  }

                  plannedProducts[donor.product_id] = true;

                  const replacementQuota = {
                    id: pregnancyQuotaVariant.variantId,
                    quantity: 1,
                    properties: {
                      __variant_id: donor.id,
                      __line_item_id: donor.id,
                      __line_item_key: donor.key,
                      __line_item_local_id: donor.properties.__item_local_id,
                      'For': donor.product_title
                    }
                  };
                  const replacementFee = app.pregnancyQuotas.exclusivityFeeLine(replacementQuota.id, replacementQuota.properties);

                  add.push(replacementQuota);

                  if (replacementFee) {
                    add.push(replacementFee);
                  }

                  response.items.forEach(donor2 => {
                    if (donor.product_id == donor2.product_id
                      && donor2.properties
                    ) {
                      donor2.properties.__pregnancy_quota = pregnancyQuotaVariant.productId + ':' + pregnancyQuotaVariant.variantId;
                      change.push({
                        id: donor2.key,
                        properties: donor2.properties,
                        quantity: donor2.quantity
                      })
                    }
                  });
                }
              }
            });
          }
        });

        if (add.length > 0) {
          this.startLoadingDonor('*');
          this.updateCartItems(remove)
            .then(() => {
              this.addCartItems(add)
                .then(() => {
                  this.changeCartItems(change)
                    .then(() => {
                      this.fetchCartSections()
                        .then(() => {
                          this.checkQuotaVariantBusy = false;
                          this.stopLoadingDonor('*');
                        })
                        .catch(error => {
                          window.location.reload();
                        })
                    })
                })
                .catch(error => {
                  console.error(error);

                  fetch(window.Shopify.routes.root + "cart/clear.js", fetchConfig("javascript"))
                    .then(() => {
                      window.location.reload();
                    });
                });
            })
            .catch(error => {
              console.error(error);

              fetch(window.Shopify.routes.root + "cart/clear.js", fetchConfig("javascript"))
                .then(() => {
                  window.location.reload();
                });
            });
        } else {
          this.checkQuotaVariantBusy = false;
        }

        return response;
      }).catch(error => {
        this.checkQuotaVariantBusy = false;
        console.error(error);
      });
    }

    updateCartItems (updates)
    {
      return new Promise((resolve, reject) => {
        if (Object.keys(updates).length > 0) {
          fetch(window.Shopify.routes.root + "cart/update.js", {
            ...fetchConfig("javascript"),
            body: JSON.stringify({
              updates: updates
            })
          }).then(res => res.json())
            .then(res => resolve(res))
            .catch(error => reject(error));
        } else {
          resolve(null);
        }
      });
    }

    changeCartItems (items)
    {
      return new Promise((resolve, reject) => {
        if (items.length > 0) {
          // Chain requests one after another
          items.reduce((previousPromise, item) => {
            return previousPromise.then(previousResult => {
              return fetch(window.Shopify.routes.root + "cart/change.js", {
                ...fetchConfig("javascript"),
                body: JSON.stringify(item)
              });
            });
          }, Promise.resolve())
            .then(res => {
              resolve(res);
            })
            .catch(error => {
              reject(error);
            });
        } else {
          resolve(null);
        }
      });
    }

    addCartItems (items)
    {
      return this.cartAdd({
        items: items
      });
    }

    checkCart ()
    {
      const setAttributes = (attributes) => {
        fetch(window.Shopify.routes.root + "cart/update.js", {
          ...fetchConfig("javascript"),
          body: JSON.stringify({
            attributes: attributes
          })
        }).then(() => {
          this.update();
        });
      };

      return this.fetchCustomCart().then(response => {
        if (!response.items || response.items.length === 0) {
          if (response.attributes && response.attributes.delivery_till) {
            const newAttributes = response.attributes;
            newAttributes.delivery_till = null;
            setAttributes(newAttributes);
          }

          return response;
        }

        let containsShipment = false;
        let containsExternalShipment = false;

        response.items.forEach(donor => {
          if (donor.product_type === 'Donor') {
            if (donor.properties
              && donor.properties.__shipping === 'clinic'
            ) {
              containsShipment = true;

              const donorClinic = donor.metafields['donor.donor_clinic'];

              if (donorClinic) {
                const shippingClinicId = typeof(donor.properties.__clinic) !== 'undefined'
                  ? donor.properties.__clinic.split(':')[0]
                  : null;

                containsExternalShipment = donorClinic.id != shippingClinicId;
              } else {
                containsExternalShipment = false;
              }
            }
          }
        });

        if (!containsShipment && response.attributes && response.attributes.delivery_till) {
          const newAttributes = response.attributes;
          newAttributes.delivery_till = null;
          setAttributes(newAttributes);
        } else if (containsShipment && !containsExternalShipment && response.attributes && response.attributes.delivery_till) {
          const newAttributes = response.attributes;
          newAttributes.delivery_till = null;
          setAttributes(newAttributes);
        }
      });
    }

    checkReservations ()
    {
      return new Promise((resolve, reject) => {
        this.fetchCustomCart().then(response => {
          if (!response.items || response.items.length === 0) {
            resolve();
            return response;
          }

          const reservations = [];

          response.items.forEach(item => {
            if (item.properties && item.properties.__reservation_id) {
              reservations.push(item.properties.__reservation_id);
            }
          });

          if (reservations.length > 0) {
            reservations.reduce((previousPromise, reservationId) => {
              return previousPromise.then(previousResult => {
                return fetch(app.apiUrl('reservations/release/' + reservationId), {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    ...app.apiHeaders()
                  },
                  body: JSON.stringify({})
                });
              });
            }, Promise.resolve())
              .then(res => {
                resolve(res);
              })
              .catch(error => {
                reject(error);
              });
          } else {
            resolve();
          }

          return response;
        });
      });
    }

    fetchCartSections ()
    {
      const cartNotification = document.querySelector("cart-notification") || document.querySelector("cart-drawer");
      const cart = document.querySelector("cart-items");

      return fetch(window.Shopify.routes.root + "?" + app.getQueryParamsForCartSectionsRender())
        .then(response => response.json())
        .then(response => {
          cartNotification.renderContents({
            items: [],
            sections: response
          }, true);
          cart.renderContents({
            sections: response
          });
          this.initAfterSectionsRender();
        });
    }

    fetchCartSection (name)
    {
      const cartNotification = document.querySelector("cart-notification") || document.querySelector("cart-drawer");
      const cart = document.querySelector("cart-items");

      return fetch(window.Shopify.routes.root + "?sections=" + name)
        .then(response => response.json())
        .then(response => {
          cartNotification.renderContents({
            items: [],
            sections: response
          }, true);
          cart.renderContents({
            sections: response
          });
          this.initAfterSectionsRender();
        });
    }

    initExpressShippingBanner ()
    {
      const expressProduct = window.theme && window.theme.expressShippingVariant;
      if (!expressProduct) return;

      this.fetchCart().then(cart => {
        const hasExpress = cart.items.some(item =>
          item.product_id === expressProduct.productId &&
          item.properties &&
          item.properties.__express_shipping === 'true'
        );

        const banner = document.querySelector('#express-shipping-banner');
        if (banner) {
          banner.classList.toggle('hidden', !hasExpress);
        }
      });
    }

    showExpressShippingConfirmation ()
    {
      return new Promise((resolve) => {
        const popup = document.querySelector('#express-shipping-popup');
        if (!popup) {
          resolve(window.confirm(window.cartStrings.express_shipping_confirm));
          return;
        }

        popup.open();

        const confirmBtn = popup.querySelector('.express-shipping-confirm');
        const cancelBtn = popup.querySelector('.express-shipping-cancel');
        const closeBtn = popup.querySelector('.popup-close');

        const cleanup = () => {
          confirmBtn.removeEventListener('click', onConfirm);
          cancelBtn.removeEventListener('click', onCancel);
          if (closeBtn) closeBtn.removeEventListener('click', onCancel);
          popup.close();
        };

        const onConfirm = () => { cleanup(); resolve(true); };
        const onCancel = () => { cleanup(); resolve(false); };

        confirmBtn.addEventListener('click', onConfirm);
        cancelBtn.addEventListener('click', onCancel);
        if (closeBtn) closeBtn.addEventListener('click', onCancel);
      });
    }

    async isExpressShippingInCart ()
    {
      const expressProduct = window.theme && window.theme.expressShippingVariant;
      if (!expressProduct) return false;

      const cart = await this.fetchCart();
      return cart.items.some(item =>
        item.product_id === expressProduct.productId &&
        item.properties &&
        item.properties.__express_shipping === 'true'
      );
    }

    manageExpressShipping (cart, isExpressDate)
    {
      const expressProduct = window.theme && window.theme.expressShippingVariant;
      if (!expressProduct) return false;

      const existingExpress = cart.items.find(item =>
        item.product_id === expressProduct.productId &&
        item.properties &&
        item.properties.__express_shipping === 'true'
      );

      const banner = document.querySelector('#express-shipping-banner');

      if (isExpressDate && !existingExpress) {
        this.cartAdd({
          items: [{
            id: expressProduct.variantId,
            quantity: 1,
            properties: {
              __express_shipping: 'true',
              'For': window.cartStrings.express_shipping_label || 'Express Delivery'
            }
          }]
        }).then(() => {
          if (banner) banner.classList.remove('hidden');
          this.fetchCartSection('order-summary').finally(() => {
            this.stopLoadingClinic('deliver-date');
          });
          this.update();
        }).catch(error => {
          console.error(error);
          this.stopLoadingClinic('deliver-date');
          this.update();
        });
        return true;
      } else if (!isExpressDate && existingExpress) {
        this.cartChange({
          id: existingExpress.key,
          quantity: 0
        }).then(() => {
          if (banner) banner.classList.add('hidden');
          this.fetchCartSection('order-summary').finally(() => {
            this.stopLoadingClinic('deliver-date');
          });
          this.update();
        }).catch(error => {
          console.error(error);
          this.stopLoadingClinic('deliver-date');
          this.update();
        });
        return true;
      }

      // No change needed for express shipping
      if (banner) {
        banner.classList.toggle('hidden', !isExpressDate);
      }
      return false;
    }

    removeExpressShipping ()
    {
      const expressProduct = window.theme && window.theme.expressShippingVariant;
      if (!expressProduct) return;

      this.startLoadingClinic('deliver-date');

      this.fetchCart().then(cart => {
        const existingExpress = cart.items.find(item =>
          item.product_id === expressProduct.productId &&
          item.properties &&
          item.properties.__express_shipping === 'true'
        );

        if (!existingExpress) {
          this.stopLoadingClinic('deliver-date');
          return;
        }

        this.cartChange({
          id: existingExpress.key,
          quantity: 0
        }).then(() => {
          const banner = document.querySelector('#express-shipping-banner');
          if (banner) banner.classList.add('hidden');

          // Clear delivery date
          const delivery = this.querySelector('[name="delivery_till"]');
          if (delivery) {
            delivery.value = '';
            if (this.deliveryDatePicker) {
              this.deliveryDatePicker.clearSelection();
            }
          }

          // Update delivery_till attribute
          fetch(window.Shopify.routes.root + "cart/update.js", {
            ...fetchConfig("javascript"),
            body: JSON.stringify({
              attributes: { delivery_till: '' }
            })
          }).then(() => {
            this.fetchCartSection('order-summary').finally(() => {
              this.stopLoadingClinic('deliver-date');
            });
            this.update();
          });
        }).catch(error => {
          console.error(error);
          this.stopLoadingClinic('deliver-date');
          this.update();
        });
      });
    }

    cartAdd (body)
    {
      return fetch(window.Shopify.routes.root + "cart/add.js", {
        ...fetchConfig("javascript"),
        body: JSON.stringify(body)
      }).then(response => response.json());
    }

    cartChange (body)
    {
      return fetch(window.Shopify.routes.root + "cart/change.js", {
        ...fetchConfig("javascript"),
        body: JSON.stringify(body)
      }).then(res => res.json());
    }

    fetchCart ()
    {
      return fetch(window.Shopify.routes.root + "cart.json", fetchConfig("javascript"))
        .then(response => response.json());
    }

    fetchCustomCart (force = true)
    {
      if (this.currentFetchCustomCart) {
        return this.currentFetchCustomCart;
      }

      if (!force && this.currentCustomCart) {
        return new Promise(resolve => {
          resolve(this.currentCustomCart);
        });
      }

      this.currentFetchCustomCart = fetch(window.Shopify.routes.root + "cart?view=custom")
        .then(response => response.json())
        .then(response => {
          this.currentFetchCustomCart = null
          this.currentCustomCart = response;

          return response;
        });

      return this.currentFetchCustomCart;
    }

    getSectionsToRender ()
    {
      return [{
        id: "main-cart-items",
        section: document.getElementById("main-cart-items").dataset.id,
        selector: ".js-contents"
      }, {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section"
      }, {
        id: "cart-live-region-text",
        section: "cart-live-region-text",
        selector: ".shopify-section"
      }, {
        id: "order-summary",
        section: document.getElementById("order-summary").dataset.id,
        selector: ".js-contents"
      }]
    }
  });
}

function checkWeekendInRange(startDate, numberOfDays) {
  const start = new Date(startDate);
  for (let i = 0; i < numberOfDays; i++) {
      const currentDate = new Date(start);
      currentDate.setDate(start.getDate() + i);
      const dayOfWeek = currentDate.getDay();
      if (dayOfWeek === 0 || dayOfWeek === 6) { // 0 = Sunday, 6 = Saturday
          numberOfDays++;
      }
  }
  return numberOfDays;
}
